import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  Workflow, 
  Database, 
  Cloud,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Save,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Search,
  ArrowRight,
  Link2,
  Eye,
  Building2,
  Filter,
  X,
  Sparkles,
  Check,
  RefreshCw,
  Key,
  Settings,
  Edit,
  FileCode,
  Download,
  Copy,
  Code
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import api from "@/services/api";
import { DataPreviewDialog } from "@/components/DataPreviewDialog";
import GoldStageCardV2 from "@/components/GoldStageCardV2";
import SQLImportModal from "@/components/SQLImportModal";

// Debounce utility for performance optimization
const useDebounce = (callback, delay) => {
  const timeoutRef = useCallback(() => {
    let timeoutId = null;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(...args), delay);
    };
  }, [callback, delay]);
  return timeoutRef();
};

// Debounced Input component - updates parent only on blur or after delay
// This prevents re-rendering the entire form on every keystroke
const DebouncedInput = memo(({ value, onChange, delay = 300, ...props }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [isFocused, setIsFocused] = useState(false);
  const timeoutRef = React.useRef(null);
  
  // Only sync from parent when not focused (to avoid cursor jumping)
  React.useEffect(() => {
    if (!isFocused && value !== localValue) {
      setLocalValue(value || '');
    }
  }, [value, isFocused]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Debounce the parent update
    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, delay);
  };
  
  const handleFocus = () => setIsFocused(true);
  
  // Update parent immediately on blur
  const handleBlur = () => {
    setIsFocused(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (localValue !== value) {
      onChange(localValue);
    }
  };
  
  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return (
    <Input
      {...props}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
});

DebouncedInput.displayName = 'DebouncedInput';

// Debounced Textarea component - same as DebouncedInput but for textarea
const DebouncedTextarea = memo(({ value, onChange, delay = 300, ...props }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [isFocused, setIsFocused] = useState(false);
  const timeoutRef = React.useRef(null);
  
  // Only sync from parent when not focused
  React.useEffect(() => {
    if (!isFocused && value !== localValue) {
      setLocalValue(value || '');
    }
  }, [value, isFocused]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, delay);
  };
  
  const handleFocus = () => setIsFocused(true);
  
  const handleBlur = () => {
    setIsFocused(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (localValue !== value) {
      onChange(localValue);
    }
  };
  
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return (
    <Textarea
      {...props}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
});

DebouncedTextarea.displayName = 'DebouncedTextarea';

// Memoized Field Mapping Row - only re-renders when its own field data changes
const FieldMappingRow = memo(({ 
  field, 
  index, 
  availableFields, 
  onUpdate, 
  onRemove 
}) => {
  // Local state for all field values to prevent parent re-renders
  const [localField, setLocalField] = useState(field);
  const [isFocused, setIsFocused] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(!!field.default_value);
  const updateTimeoutRef = React.useRef(null);
  
  // Sync with external changes when not focused
  React.useEffect(() => {
    if (!isFocused) {
      setLocalField(field);
    }
  }, [field, isFocused]);
  
  // Debounced update to parent
  const debouncedUpdate = useCallback((updates) => {
    const newField = { ...localField, ...updates };
    setLocalField(newField);
    
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      onUpdate(index, updates);
    }, 300);
  }, [localField, index, onUpdate]);
  
  // Immediate update for select changes (no need to debounce dropdowns)
  const immediateUpdate = useCallback((updates) => {
    setLocalField(prev => ({ ...prev, ...updates }));
    onUpdate(index, updates);
  }, [index, onUpdate]);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);
  
  // Check if current data type is numeric
  const isNumericType = ['INT64', 'FLOAT64', 'NUMERIC'].includes(localField.data_type);
  
  return (
    <div className="bg-white p-2 rounded border space-y-2">
      <div className="grid grid-cols-12 gap-2 items-center">
        {availableFields.length > 0 ? (
          <Select 
            value={localField.source_field || ""} 
            onValueChange={(value) => immediateUpdate({ source_field: value, target_field: localField.target_field || value })}
          >
            <SelectTrigger className="col-span-3">
              <SelectValue placeholder="Select field" />
            </SelectTrigger>
            <SelectContent>
              {availableFields.map((f) => (
                <SelectItem key={f.name} value={f.name}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            className="col-span-3"
            value={localField.source_field || ''}
            onChange={(e) => debouncedUpdate({ source_field: e.target.value })}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Field name"
          />
        )}
        <ArrowRight className="col-span-1 w-4 h-4 text-muted-foreground mx-auto" />
        <Input
          className="col-span-2"
          value={localField.target_field || ''}
          onChange={(e) => debouncedUpdate({ target_field: e.target.value })}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Target"
        />
        <Select 
          value={localField.data_type || "STRING"} 
          onValueChange={(value) => {
            immediateUpdate({ data_type: value });
            // Show advanced options for numeric types
            if (['INT64', 'FLOAT64', 'NUMERIC'].includes(value)) {
              setShowAdvanced(true);
            }
          }}
        >
          <SelectTrigger className="col-span-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATA_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select 
          value={localField.transformation || "none"} 
          onValueChange={(value) => immediateUpdate({ transformation: value })}
        >
          <SelectTrigger className="col-span-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRANSFORMATIONS.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="col-span-2 flex gap-1">
          {isNumericType && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9"
              onClick={() => setShowAdvanced(!showAdvanced)}
              title="Default value for NULL"
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => onRemove(index)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Advanced Options - Default Value */}
      {showAdvanced && isNumericType && (
        <div className="flex items-center gap-2 pl-2 pt-1 border-t">
          <span className="text-xs text-muted-foreground whitespace-nowrap">Default if NULL:</span>
          <Input
            className="w-24 h-8 text-sm"
            value={localField.default_value || ''}
            onChange={(e) => debouncedUpdate({ default_value: e.target.value })}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="e.g., 0"
          />
          <span className="text-xs text-muted-foreground">
            {localField.default_value ? `✓ NULL → ${localField.default_value}` : '(empty = keep NULL)'}
          </span>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if field data actually changed
  return (
    prevProps.index === nextProps.index &&
    prevProps.field.source_field === nextProps.field.source_field &&
    prevProps.field.target_field === nextProps.field.target_field &&
    prevProps.field.data_type === nextProps.field.data_type &&
    prevProps.field.transformation === nextProps.field.transformation &&
    prevProps.field.default_value === nextProps.field.default_value &&
    prevProps.availableFields === nextProps.availableFields
  );
});

FieldMappingRow.displayName = 'FieldMappingRow';

const TRANSFORMATIONS = [
  { value: "none", label: "None" },
  { value: "UPPER", label: "UPPER" },
  { value: "LOWER", label: "LOWER" },
  { value: "TRIM", label: "TRIM" },
];

const DATA_TYPES = [
  { value: "STRING", label: "STRING" },
  { value: "INT64", label: "INT64 (Integer)" },
  { value: "FLOAT64", label: "FLOAT64 (Decimal)" },
  { value: "DATE", label: "DATE" },
  { value: "TIMESTAMP", label: "TIMESTAMP" },
  { value: "BOOL", label: "BOOLEAN" },
];

const JOIN_TYPES = [
  { value: "INNER", label: "INNER JOIN" },
  { value: "LEFT", label: "LEFT JOIN" },
  { value: "RIGHT", label: "RIGHT JOIN" },
  { value: "FULL", label: "FULL OUTER JOIN" },
];

// Convert visual filters to SQL WHERE clause
const convertVisualFiltersToSQL = (filters) => {
  if (!filters || filters.length === 0) return "";
  
  const conditions = filters
    .filter(f => f.field) // Only process filters with a field selected
    .map(f => {
      const field = f.field;
      const value = f.value || "";
      
      switch (f.operator) {
        case "equals":
          return `${field} = '${value.replace(/'/g, "''")}'`;
        case "not_equals":
          return `${field} != '${value.replace(/'/g, "''")}'`;
        case "greater":
          return `${field} > '${value.replace(/'/g, "''")}'`;
        case "greater_eq":
          return `${field} >= '${value.replace(/'/g, "''")}'`;
        case "less":
          return `${field} < '${value.replace(/'/g, "''")}'`;
        case "less_eq":
          return `${field} <= '${value.replace(/'/g, "''")}'`;
        case "contains":
          return `${field} LIKE '%${value.replace(/'/g, "''")}%'`;
        case "starts_with":
          return `${field} LIKE '${value.replace(/'/g, "''")}%'`;
        case "ends_with":
          return `${field} LIKE '%${value.replace(/'/g, "''")}'`;
        case "is_null":
          return `${field} IS NULL`;
        case "is_not_null":
          return `${field} IS NOT NULL`;
        case "in_list":
          // Split by comma and trim each value
          const values = value.split(",").map(v => `'${v.trim().replace(/'/g, "''")}'`).join(", ");
          return `${field} IN (${values})`;
        default:
          return null;
      }
    })
    .filter(Boolean);
  
  return conditions.join(" AND ");
};

// Convert value mappings to SQL CASE expressions (as additional SELECT fields)
const convertValueMappingsToFields = (mappings) => {
  if (!mappings || mappings.length === 0) return [];
  
  return mappings
    .filter(m => m.source_field && m.target_field && m.rules.some(r => r.when_value && r.then_value))
    .map(m => {
      let caseExpr = `CASE`;
      m.rules.forEach(rule => {
        if (rule.when_value && rule.then_value) {
          caseExpr += ` WHEN ${m.source_field} = '${rule.when_value.replace(/'/g, "''")}' THEN '${rule.then_value.replace(/'/g, "''")}'`;
        }
      });
      if (m.else_value) {
        caseExpr += ` ELSE '${m.else_value.replace(/'/g, "''")}'`;
      } else {
        caseExpr += ` ELSE ${m.source_field}`; // Keep original value if no else
      }
      caseExpr += ` END`;
      
      return {
        source_field: caseExpr,
        target_field: m.target_field,
        transformation: "none",
        data_type: "STRING",
        is_case_expression: true
      };
    });
};

// Searchable Table Selector Component
const TableSearchSelector = ({ 
  connectionId, 
  schema, 
  value, 
  onChange, 
  disabled,
  testId,
  sourceType = "oracle",
  placeholder = "Search and select table..."
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const searchTables = useCallback(async (searchTerm) => {
    if (!connectionId || !schema) return;
    setLoading(true);
    try {
      let data;
      if (sourceType === 'oracle') {
        data = await api.getOracleTables(connectionId, schema, searchTerm || null, 50);
        setTables(data.tables || []);
      } else if (sourceType === 'sqlserver') {
        data = await api.getSQLServerTables(connectionId, schema);
        // Filter by search term client-side for SQL Server
        const filtered = searchTerm 
          ? (data.tables || []).filter(t => t.table_name?.toLowerCase().includes(searchTerm.toLowerCase()))
          : (data.tables || []);
        setTables(filtered.slice(0, 50));
      } else if (sourceType === 'postgresql') {
        data = await api.getPostgreSQLTables(connectionId, schema);
        // Filter by search term client-side for PostgreSQL
        const filtered = searchTerm 
          ? (data.tables || []).filter(t => t.table_name?.toLowerCase().includes(searchTerm.toLowerCase()))
          : (data.tables || []);
        setTables(filtered.slice(0, 50));
      }
    } catch (error) {
      console.error("Failed to search tables:", error);
      setTables([]);
    } finally {
      setLoading(false);
    }
  }, [connectionId, schema, sourceType]);

  useEffect(() => {
    if (open && connectionId && schema) {
      searchTables("");
    }
  }, [open, connectionId, schema, searchTables]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      searchTables(val);
    }, 300);
    setSearchTimeout(timeout);
  };

  const handleSelect = (tableName) => {
    onChange(tableName);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled || !connectionId || !schema}
          data-testid={testId}
        >
          {value || placeholder}
          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Type to search tables..."
            value={search}
            onChange={handleSearchChange}
            className="h-9"
            autoFocus
          />
        </div>
        <ScrollArea className="h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : tables.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {search ? "No tables found" : "Type to search tables"}
            </div>
          ) : (
            <div className="p-1">
              {tables.map((table) => (
                <button
                  key={table.table_name}
                  onClick={() => handleSelect(table.table_name)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors ${
                    value === table.table_name ? "bg-primary text-primary-foreground" : "text-foreground"
                  }`}
                >
                  <div className="font-medium text-inherit">{table.table_name}</div>
                  {table.row_count > 0 && (
                    <div className={`text-xs ${value === table.table_name ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                      ~{table.row_count.toLocaleString()} rows
                    </div>
                  )}
                </button>
              ))}
              {tables.length >= 50 && (
                <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t mt-1">
                  Showing first 50 results. Type more to narrow down.
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

// Multi-Table Search Selector for Bronze - Select multiple tables at once
const MultiTableSearchSelector = ({ 
  connectionId, 
  schema, 
  selectedTables,
  onChange, 
  disabled,
  sourceType = "oracle"
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const searchTables = useCallback(async (searchTerm) => {
    if (!connectionId || !schema) return;
    setLoading(true);
    try {
      let data;
      if (sourceType === 'oracle') {
        data = await api.getOracleTables(connectionId, schema, searchTerm || null, 100);
        setTables(data.tables || []);
      } else if (sourceType === 'sqlserver') {
        data = await api.getSQLServerTables(connectionId, schema);
        const filtered = searchTerm 
          ? (data.tables || []).filter(t => t.table_name?.toLowerCase().includes(searchTerm.toLowerCase()))
          : (data.tables || []);
        setTables(filtered.slice(0, 100));
      } else if (sourceType === 'postgresql') {
        data = await api.getPostgreSQLTables(connectionId, schema);
        const filtered = searchTerm 
          ? (data.tables || []).filter(t => t.table_name?.toLowerCase().includes(searchTerm.toLowerCase()))
          : (data.tables || []);
        setTables(filtered.slice(0, 100));
      }
    } catch (error) {
      console.error("Failed to search tables:", error);
      setTables([]);
    } finally {
      setLoading(false);
    }
  }, [connectionId, schema, sourceType]);

  useEffect(() => {
    if (open && connectionId && schema) {
      searchTables("");
    }
  }, [open, connectionId, schema, searchTables]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      searchTables(val);
    }, 300);
    setSearchTimeout(timeout);
  };

  const handleToggleTable = (tableName) => {
    if (selectedTables.includes(tableName)) {
      onChange(selectedTables.filter(t => t !== tableName));
    } else {
      onChange([...selectedTables, tableName]);
    }
  };

  const handleSelectAll = () => {
    const allTableNames = tables.map(t => t.table_name);
    const newSelection = [...new Set([...selectedTables, ...allTableNames])];
    onChange(newSelection);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-auto min-h-10"
          disabled={disabled || !connectionId || !schema}
          data-testid="multi-table-selector"
        >
          <span className="truncate">
            {selectedTables.length === 0 
              ? "Search and select tables..." 
              : `${selectedTables.length} table(s) selected`
            }
          </span>
          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <div className="p-2 border-b space-y-2">
          <Input
            placeholder="Type to search tables..."
            value={search}
            onChange={handleSearchChange}
            className="h-9"
            autoFocus
          />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSelectAll} className="text-xs">
              Select All Visible
            </Button>
            <Button variant="outline" size="sm" onClick={handleClearAll} className="text-xs">
              Clear All
            </Button>
            <span className="text-xs text-muted-foreground ml-auto self-center">
              {selectedTables.length} selected
            </span>
          </div>
        </div>
        <ScrollArea className="h-[350px]">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : tables.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {search ? "No tables found" : "Type to search tables"}
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {tables.map((table) => {
                const isSelected = selectedTables.includes(table.table_name);
                return (
                  <label
                    key={table.table_name}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                      isSelected ? "bg-orange-100 border border-orange-300" : ""
                    }`}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggleTable(table.table_name)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{table.table_name}</div>
                      {table.row_count > 0 && (
                        <div className="text-xs text-muted-foreground">
                          ~{table.row_count.toLocaleString()} rows
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
              {tables.length >= 100 && (
                <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t mt-2">
                  Showing first 100 results. Type more to narrow down.
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

// Bronze Stage Card - Supports both single table and multi-table batch selection
// Wrapped with memo to prevent unnecessary re-renders when other stages change
const BronzeStageCard = memo(({ 
  stage, 
  stageIndex,
  connectionId,
  bigqueryConnectionId,
  schemas,
  columns, 
  onUpdate, 
  onRemove, 
  onTableChange,
  onAddMultipleBronzeStages,
  loadingColumns,
  sourceType = "oracle"
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [useMultiTable, setUseMultiTable] = useState(false);
  const [selectedTables, setSelectedTables] = useState([]);
  
  const handleFieldToggle = (column) => {
    const existingField = stage.fields.find(f => f.source_field === column.column_name);
    
    if (existingField) {
      onUpdate({
        ...stage,
        fields: stage.fields.filter(f => f.source_field !== column.column_name)
      });
    } else {
      onUpdate({
        ...stage,
        fields: [...stage.fields, {
          source_field: column.column_name,
          target_field: column.column_name,
          transformation: "none"
        }]
      });
    }
  };
  
  const selectAllFields = () => {
    onUpdate({
      ...stage,
      fields: columns.map(col => ({
        source_field: col.column_name,
        target_field: col.column_name,
        transformation: "none"
      }))
    });
  };
  
  const clearAllFields = () => {
    onUpdate({ ...stage, fields: [] });
  };

  const handleAddSelectedTables = () => {
    if (selectedTables.length === 0 || !stage.source_schema) return;
    onAddMultipleBronzeStages(stage.source_schema, selectedTables);
    setSelectedTables([]);
    setUseMultiTable(false);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="stage-card bronze shadow-soft border-2 border-orange-300 bg-orange-50/30">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-orange-500 text-white">Bronze Stage</Badge>
                <span className="text-sm text-muted-foreground">
                  {stage.source_table ? `${stage.source_schema}.${stage.source_table}` : "Raw data extraction"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => { e.stopPropagation(); onRemove(); }}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Schema Selection */}
            <div className="space-y-2">
              <Label>Source Schema (Oracle)</Label>
              <Select 
                value={stage.source_schema || ""} 
                onValueChange={(value) => {
                  onUpdate({ ...stage, source_schema: value, source_table: "", fields: [] });
                  setSelectedTables([]);
                }}
              >
                <SelectTrigger data-testid={`stage-${stageIndex}-source-schema`}>
                  <SelectValue placeholder="Select schema" />
                </SelectTrigger>
                <SelectContent>
                  {schemas.map((schema) => (
                    <SelectItem key={schema.schema_name} value={schema.schema_name}>
                      {schema.schema_name} ({schema.table_count} tables)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Multi-table toggle */}
            <div className="flex items-center gap-3 p-3 bg-orange-100 rounded-lg">
              <Checkbox 
                id={`bronze-multi-${stageIndex}`}
                checked={useMultiTable}
                onCheckedChange={setUseMultiTable}
              />
              <Label htmlFor={`bronze-multi-${stageIndex}`} className="cursor-pointer">
                <span className="font-medium">Select Multiple Tables</span>
                <p className="text-xs text-muted-foreground">Add multiple tables at once - each becomes a separate Bronze stage</p>
              </Label>
            </div>

            {useMultiTable ? (
              /* Multi-Table Selection Mode */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Tables (from {stage.source_schema || "schema"})</Label>
                  <MultiTableSearchSelector
                    connectionId={connectionId}
                    schema={stage.source_schema}
                    selectedTables={selectedTables}
                    onChange={setSelectedTables}
                    disabled={!stage.source_schema}
                    sourceType={sourceType}
                  />
                </div>
                
                {selectedTables.length > 0 && (
                  <div className="space-y-3">
                    <Label>Selected Tables ({selectedTables.length})</Label>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-white">
                      <div className="flex flex-wrap gap-2">
                        {selectedTables.map((tableName) => (
                          <Badge 
                            key={tableName} 
                            variant="secondary"
                            className="bg-orange-100 text-orange-800 hover:bg-orange-200 cursor-pointer"
                            onClick={() => setSelectedTables(prev => prev.filter(t => t !== tableName))}
                          >
                            {tableName}
                            <span className="ml-1 text-orange-500">×</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Target tables will be auto-named: raw_{"{table_name}"} (lowercase)
                    </p>
                    <Button 
                      onClick={handleAddSelectedTables}
                      className="w-full bg-orange-500 hover:bg-orange-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add {selectedTables.length} Bronze Stage{selectedTables.length > 1 ? 's' : ''}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              /* Single Table Selection Mode */
              <>
                {/* Table Selection */}
                <div className="space-y-2">
                  <Label>Source Table ({sourceType === 'oracle' ? 'Oracle' : sourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'})</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <TableSearchSelector
                        connectionId={connectionId}
                        schema={stage.source_schema}
                        value={stage.source_table}
                        sourceType={sourceType}
                        onChange={(tableName) => {
                          onUpdate({ ...stage, source_table: tableName, fields: [] });
                          onTableChange(tableName, stage.source_schema);
                        }}
                        disabled={!stage.source_schema}
                        testId={`stage-${stageIndex}-source-table`}
                      />
                    </div>
                    {stage.source_table && stage.source_schema && (
                      <DataPreviewDialog
                        type="oracle"
                        connectionId={connectionId}
                        schema={stage.source_schema}
                        tableName={stage.source_table}
                      />
                    )}
                  </div>
                </div>
                
                {/* Target Table Name */}
                <div className="space-y-2">
                  <Label>Target Table Name (BigQuery bronze_layer)</Label>
                  <div className="flex gap-2">
                    <Input
                      className="flex-1"
                      value={stage.target_table || ""}
                      onChange={(e) => onUpdate({ ...stage, target_table: e.target.value })}
                      placeholder="e.g., raw_customers"
                      data-testid={`stage-${stageIndex}-target-table`}
                    />
                    {stage.target_table && bigqueryConnectionId && (
                      <DataPreviewDialog
                        type="bronze"
                        bigqueryConnectionId={bigqueryConnectionId}
                        tableName={stage.target_table}
                      />
                    )}
                  </div>
                </div>

                {/* Key Column (for CDC/Automation) */}
                <div className="space-y-2">
                  <Label>Primary Key Column(s) <span className="text-muted-foreground font-normal">(for CDC &amp; automation)</span></Label>
                  <Input
                    value={stage.key_column || ""}
                    onChange={(e) => onUpdate({ ...stage, key_column: e.target.value })}
                    placeholder="e.g., BILL_NO, BILL_SER (comma-separated for composite keys)"
                    data-testid={`stage-${stageIndex}-key-column`}
                  />
                  <p className="text-xs text-muted-foreground">Used as MERGE key in automation SQL. Supports composite keys (e.g., BILL_NO, BILL_SER). Leave empty for auto-detect.</p>
                </div>
                
                {/* Field Selection */}
                {columns.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Select Fields ({stage.fields.length} of {columns.length})</Label>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={selectAllFields}>
                          Select All
                        </Button>
                        <Button variant="outline" size="sm" onClick={clearAllFields}>
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="border rounded-lg max-h-64 overflow-y-auto">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-1 p-3">
                        {columns.map((col) => {
                          // Support both column_name and name for compatibility
                          const colName = col.column_name || col.name || 'unknown';
                          const colType = col.data_type || col.type || 'STRING';
                          const isSelected = stage.fields.some(f => f.source_field === colName);
                          return (
                            <label
                              key={colName}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted transition-colors ${isSelected ? 'bg-orange-100' : ''}`}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleFieldToggle({ ...col, column_name: colName, data_type: colType })}
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-mono truncate">{colName}</p>
                                <p className="text-xs text-muted-foreground">{colType}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : stage.fields.length > 0 ? (
                  /* Show saved fields when columns haven't loaded yet */
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Selected Fields ({stage.fields.length})</Label>
                      <Button variant="outline" size="sm" onClick={clearAllFields}>
                        Clear
                      </Button>
                    </div>
                    <div className="border rounded-lg max-h-64 overflow-y-auto bg-orange-50">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-1 p-3">
                        {stage.fields.map((field) => (
                          <div
                            key={field.source_field}
                            className="flex items-center gap-2 p-2 rounded bg-orange-100"
                          >
                            <Checkbox checked={true} disabled />
                            <div className="min-w-0">
                              <p className="text-sm font-mono truncate">{field.source_field}</p>
                              <p className="text-xs text-muted-foreground">{field.transformation || 'none'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-amber-600">
                      ⚠️ To add or modify fields, reconnect the source database from the Connections page.
                    </p>
                  </div>
                ) : null}
                
                {loadingColumns && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading columns...
                  </div>
                )}
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

// Add display name for debugging
BronzeStageCard.displayName = 'BronzeStageCard';

// Silver Stage Card - Clean and transform a single Bronze table
// Wrapped with memo to prevent unnecessary re-renders
const SilverStageCard = memo(({ 
  stage, 
  stageIndex,
  bronzeTables,
  bronzeFieldsByTable,
  bigqueryConnectionId,
  onUpdate, 
  onRemove
}) => {
  const [isOpen, setIsOpen] = useState(true);
  
  // Get available fields from the selected bronze table
  const availableFields = useMemo(() => 
    stage.source_table ? (bronzeFieldsByTable[stage.source_table] || []) : [],
    [stage.source_table, bronzeFieldsByTable]
  );
  
  const addFieldMapping = useCallback(() => {
    onUpdate({
      ...stage,
      fields: [...stage.fields, { source_field: "", target_field: "", transformation: "none", data_type: "STRING" }]
    });
  }, [stage, onUpdate]);
  
  // Memoized updateField - critical for performance with many fields
  const updateField = useCallback((index, updates) => {
    const newFields = [...stage.fields];
    newFields[index] = { ...newFields[index], ...updates };
    // Auto-fill target field with source field name if target is empty
    if (updates.source_field && !newFields[index].target_field) {
      newFields[index].target_field = updates.source_field;
    }
    onUpdate({ ...stage, fields: newFields });
  }, [stage, onUpdate]);
  
  // Memoized removeField
  const removeField = useCallback((index) => {
    onUpdate({ ...stage, fields: stage.fields.filter((_, i) => i !== index) });
  }, [stage, onUpdate]);
  
  const addAllFields = useCallback(() => {
    const newFields = availableFields.map(f => ({
      source_field: f.name,
      target_field: f.name,
      transformation: "none",
      data_type: "STRING"
    }));
    onUpdate({ ...stage, fields: newFields });
  }, [availableFields, stage, onUpdate]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="stage-card silver shadow-soft border-2 border-slate-300 bg-slate-50/30">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-slate-500 text-white">Silver Stage</Badge>
                <span className="text-sm text-muted-foreground">
                  {stage.source_table ? `${stage.source_table} → ${stage.target_table || '?'}` : "Clean & transform data"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => { e.stopPropagation(); onRemove(); }}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Source Table from Bronze */}
            <div className="space-y-2">
              <Label>Source Table (from Bronze layer)</Label>
              <Select 
                value={stage.source_table || ""} 
                onValueChange={(value) => onUpdate({ ...stage, source_table: value, fields: [] })}
              >
                <SelectTrigger data-testid={`stage-${stageIndex}-source-table`}>
                  <SelectValue placeholder="Select bronze table to transform" />
                </SelectTrigger>
                <SelectContent>
                  {bronzeTables.map((table) => (
                    <SelectItem key={table} value={table}>
                      bronze_layer.{table}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {stage.source_table && bigqueryConnectionId && (
                <DataPreviewDialog
                  type="bronze"
                  bigqueryConnectionId={bigqueryConnectionId}
                  tableName={stage.source_table}
                  triggerButton={
                    <Button variant="outline" size="sm" className="mt-2">
                      <Eye className="w-4 h-4 mr-1" /> Preview Source
                    </Button>
                  }
                />
              )}
            </div>
            
            {/* Target Table Name */}
            <div className="space-y-2">
              <Label>Target Table Name (silver_layer)</Label>
              <div className="flex gap-2">
                <DebouncedInput
                  className="flex-1"
                  value={stage.target_table || ""}
                  onChange={(value) => onUpdate({ ...stage, target_table: value })}
                  placeholder="e.g., cleaned_customers"
                />
                {stage.target_table && bigqueryConnectionId && (
                  <DataPreviewDialog
                    type="silver"
                    bigqueryConnectionId={bigqueryConnectionId}
                    tableName={stage.target_table}
                  />
                )}
              </div>
            </div>
            
            {/* Field Mappings */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Field Mappings & Data Types</Label>
                {availableFields.length > 0 && (
                  <Button variant="outline" size="sm" onClick={addAllFields}>
                    Add All Fields
                  </Button>
                )}
              </div>
              {!stage.source_table ? (
                <p className="text-xs text-muted-foreground">
                  Select a source Bronze table first.
                </p>
              ) : availableFields.length === 0 ? (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠️ No fields selected in Bronze stage for &quot;{stage.source_table}&quot;. 
                  Go back to the Bronze stage and select the fields you want to include.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Select fields from {stage.source_table}. Bronze data is STRING - cast to proper types here.
                </p>
              )}
              <div className="space-y-2">
                {stage.fields.map((field, idx) => (
                  <FieldMappingRow
                    key={`field-${idx}-${field.source_field}`}
                    field={field}
                    index={idx}
                    availableFields={availableFields}
                    onUpdate={updateField}
                    onRemove={removeField}
                  />
                ))}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addFieldMapping} 
                  disabled={!stage.source_table}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Field
                </Button>
              </div>
            </div>
            
            {/* Filter - Visual Builder */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filter Conditions
                </Label>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const filters = stage.visual_filters || [];
                    onUpdate({ 
                      ...stage, 
                      visual_filters: [...filters, { field: '', operator: 'equals', value: '' }] 
                    });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Filter
                </Button>
              </div>
              
              {(stage.visual_filters && stage.visual_filters.length > 0) ? (
                <div className="space-y-2">
                  {stage.visual_filters.map((filter, filterIdx) => (
                    <div key={filterIdx} className="flex items-center gap-2 p-2 bg-white border rounded-lg">
                      {/* Field Select */}
                      <Select
                        value={filter.field}
                        onValueChange={(val) => {
                          const newFilters = [...stage.visual_filters];
                          newFilters[filterIdx] = { ...filter, field: val };
                          onUpdate({ ...stage, visual_filters: newFilters });
                        }}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableFields.map(f => (
                            <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Operator Select */}
                      <Select
                        value={filter.operator}
                        onValueChange={(val) => {
                          const newFilters = [...stage.visual_filters];
                          newFilters[filterIdx] = { ...filter, operator: val };
                          onUpdate({ ...stage, visual_filters: newFilters });
                        }}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">= Equals</SelectItem>
                          <SelectItem value="not_equals">≠ Not Equals</SelectItem>
                          <SelectItem value="greater">{'>'} Greater</SelectItem>
                          <SelectItem value="greater_eq">≥ Greater/Equal</SelectItem>
                          <SelectItem value="less">{'<'} Less</SelectItem>
                          <SelectItem value="less_eq">≤ Less/Equal</SelectItem>
                          <SelectItem value="contains">Contains</SelectItem>
                          <SelectItem value="starts_with">Starts With</SelectItem>
                          <SelectItem value="ends_with">Ends With</SelectItem>
                          <SelectItem value="is_null">Is Null</SelectItem>
                          <SelectItem value="is_not_null">Is Not Null</SelectItem>
                          <SelectItem value="in_list">In List</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Value Input (hidden for null checks) */}
                      {!['is_null', 'is_not_null'].includes(filter.operator) && (
                        <DebouncedInput
                          className="flex-1"
                          value={filter.value}
                          onChange={(value) => {
                            const newFilters = [...stage.visual_filters];
                            newFilters[filterIdx] = { ...filter, value: value };
                            onUpdate({ ...stage, visual_filters: newFilters });
                          }}
                          placeholder={filter.operator === 'in_list' ? "val1, val2, val3" : "Value"}
                        />
                      )}
                      
                      {/* Remove Button */}
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => {
                          const newFilters = stage.visual_filters.filter((_, i) => i !== filterIdx);
                          onUpdate({ ...stage, visual_filters: newFilters });
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {stage.visual_filters.length > 1 && (
                    <p className="text-xs text-muted-foreground ml-2">
                      Multiple filters are combined with AND
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No filters. All rows from the source table will be included.
                </p>
              )}
            </div>

            {/* Value Mappings (CASE WHEN transformations) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  Value Mappings
                  <span className="text-xs text-muted-foreground font-normal">(Transform values)</span>
                </Label>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const mappings = stage.value_mappings || [];
                    onUpdate({ 
                      ...stage, 
                      value_mappings: [...mappings, { 
                        source_field: '', 
                        target_field: '',
                        rules: [{ when_value: '', then_value: '' }],
                        else_value: ''
                      }] 
                    });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Mapping
                </Button>
              </div>

              {(stage.value_mappings && stage.value_mappings.length > 0) ? (
                <div className="space-y-4">
                  {stage.value_mappings.map((mapping, mapIdx) => (
                    <div key={mapIdx} className="p-3 bg-white border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-600">Mapping #{mapIdx + 1}</span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            const newMappings = stage.value_mappings.filter((_, i) => i !== mapIdx);
                            onUpdate({ ...stage, value_mappings: newMappings });
                          }}
                          className="text-destructive h-6"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      
                      {/* Source and Target Field */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Source Field</Label>
                          <Select
                            value={mapping.source_field}
                            onValueChange={(val) => {
                              const newMappings = [...stage.value_mappings];
                              newMappings[mapIdx] = { ...mapping, source_field: val, target_field: mapping.target_field || val + '_mapped' };
                              onUpdate({ ...stage, value_mappings: newMappings });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select field" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableFields.map(f => (
                                <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">New Field Name</Label>
                          <DebouncedInput
                            value={mapping.target_field}
                            onChange={(value) => {
                              const newMappings = [...stage.value_mappings];
                              newMappings[mapIdx] = { ...mapping, target_field: value };
                              onUpdate({ ...stage, value_mappings: newMappings });
                            }}
                            placeholder="e.g., payment_type"
                          />
                        </div>
                      </div>
                      
                      {/* Mapping Rules */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Rules (When → Then)</Label>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              const newMappings = [...stage.value_mappings];
                              newMappings[mapIdx] = { 
                                ...mapping, 
                                rules: [...mapping.rules, { when_value: '', then_value: '' }] 
                              };
                              onUpdate({ ...stage, value_mappings: newMappings });
                            }}
                            className="h-6 text-xs"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add Rule
                          </Button>
                        </div>
                        
                        {mapping.rules.map((rule, ruleIdx) => (
                          <div key={ruleIdx} className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-12">When</span>
                            <DebouncedInput
                              className="flex-1"
                              value={rule.when_value}
                              onChange={(value) => {
                                const newMappings = [...stage.value_mappings];
                                const newRules = [...mapping.rules];
                                newRules[ruleIdx] = { ...rule, when_value: value };
                                newMappings[mapIdx] = { ...mapping, rules: newRules };
                                onUpdate({ ...stage, value_mappings: newMappings });
                              }}
                              placeholder="e.g., 1"
                            />
                            <span className="text-xs text-muted-foreground">→</span>
                            <DebouncedInput
                              className="flex-1"
                              value={rule.then_value}
                              onChange={(value) => {
                                const newMappings = [...stage.value_mappings];
                                const newRules = [...mapping.rules];
                                newRules[ruleIdx] = { ...rule, then_value: value };
                                newMappings[mapIdx] = { ...mapping, rules: newRules };
                                onUpdate({ ...stage, value_mappings: newMappings });
                              }}
                              placeholder="e.g., Cash"
                            />
                            {mapping.rules.length > 1 && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => {
                                  const newMappings = [...stage.value_mappings];
                                  newMappings[mapIdx] = { 
                                    ...mapping, 
                                    rules: mapping.rules.filter((_, i) => i !== ruleIdx) 
                                  };
                                  onUpdate({ ...stage, value_mappings: newMappings });
                                }}
                                className="h-8 w-8 text-muted-foreground"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                        
                        {/* Else Value */}
                        <div className="flex items-center gap-2 pt-1 border-t">
                          <span className="text-xs text-muted-foreground w-12">Else</span>
                          <DebouncedInput
                            className="flex-1"
                            value={mapping.else_value}
                            onChange={(value) => {
                              const newMappings = [...stage.value_mappings];
                              newMappings[mapIdx] = { ...mapping, else_value: value };
                              onUpdate({ ...stage, value_mappings: newMappings });
                            }}
                            placeholder="Default value (or leave blank for original)"
                          />
                        </div>
                      </div>
                      
                      {/* Preview */}
                      {mapping.source_field && mapping.rules.some(r => r.when_value && r.then_value) && (
                        <div className="text-xs bg-slate-100 p-2 rounded font-mono">
                          <span className="text-slate-500">Example: </span>
                          {mapping.source_field} = &quot;{mapping.rules[0]?.when_value}&quot; → {mapping.target_field} = &quot;{mapping.rules[0]?.then_value}&quot;
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No value mappings. Use this to transform values like: invoice_no = 1 → payment_type = &quot;Cash&quot;
                </p>
              )}
            </div>

            {/* Generated SQL Preview */}
            {((stage.visual_filters && stage.visual_filters.length > 0) || 
              (stage.value_mappings && stage.value_mappings.length > 0)) && (
              <div className="space-y-2 pt-4 border-t">
                <Label className="flex items-center gap-2 text-slate-600">
                  <Eye className="w-4 h-4" />
                  Generated SQL Preview
                </Label>
                <div className="bg-slate-900 text-slate-100 p-3 rounded-lg font-mono text-xs overflow-x-auto">
                  {/* Show WHERE clause preview */}
                  {stage.visual_filters && stage.visual_filters.length > 0 && (
                    <div className="mb-2">
                      <span className="text-blue-400">WHERE </span>
                      <span className="text-green-400">
                        {convertVisualFiltersToSQL(stage.visual_filters) || <span className="text-slate-500 italic">Select field to see preview</span>}
                      </span>
                    </div>
                  )}
                  {/* Show CASE expressions preview */}
                  {stage.value_mappings && stage.value_mappings.length > 0 && 
                   stage.value_mappings.some(m => m.source_field && m.rules.some(r => r.when_value)) && (
                    <div>
                      <span className="text-slate-500">-- Value Mappings as CASE expressions:</span>
                      {stage.value_mappings
                        .filter(m => m.source_field && m.rules.some(r => r.when_value))
                        .map((m, idx) => (
                          <div key={idx} className="mt-1">
                            <span className="text-purple-400">CASE </span>
                            {m.rules.filter(r => r.when_value).map((r, rIdx) => (
                              <span key={rIdx}>
                                <span className="text-blue-400">WHEN </span>
                                <span className="text-yellow-300">{m.source_field}</span>
                                <span className="text-slate-300"> = </span>
                                <span className="text-green-400">&apos;{r.when_value}&apos;</span>
                                <span className="text-blue-400"> THEN </span>
                                <span className="text-green-400">&apos;{r.then_value}&apos;</span>
                                {rIdx < m.rules.filter(r2 => r2.when_value).length - 1 && " "}
                              </span>
                            ))}
                            {m.else_value && (
                              <>
                                <span className="text-blue-400"> ELSE </span>
                                <span className="text-green-400">&apos;{m.else_value}&apos;</span>
                              </>
                            )}
                            <span className="text-purple-400"> END</span>
                            <span className="text-blue-400"> AS </span>
                            <span className="text-yellow-300">{m.target_field}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  This SQL will be applied when running the pipeline
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

// Add display name for debugging
SilverStageCard.displayName = 'SilverStageCard';

// Gold Stage Card - Join multiple Silver tables
// Wrapped with memo to prevent unnecessary re-renders
const GoldStageCard = memo(({ 
  stage, 
  stageIndex,
  silverTables,
  goldTables, // NEW: List of Gold tables created by earlier stages
  silverFieldsByTable,
  goldFieldsByTable, // NEW: Fields from Gold tables
  bigqueryConnectionId,
  onUpdate, 
  onRemove
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showUnionFieldsModal, setShowUnionFieldsModal] = useState(null); // Index of table to edit fields for
  const [fetchedGoldFields, setFetchedGoldFields] = useState({}); // Cache for API-fetched Gold table fields
  
  const sourceTables = stage.source_tables || [];
  const joinConditions = stage.join_conditions || [];
  const combineMode = stage.combine_mode || 'join';
  
  // Fetch Gold table columns from BigQuery when needed
  useEffect(() => {
    const fetchMissingGoldFields = async () => {
      for (const st of sourceTables) {
        if ((st.source_layer === 'gold') && st.table_name && bigqueryConnectionId) {
          // Check if we already have fields for this Gold table
          const existingFields = (goldFieldsByTable || {})[st.table_name] || [];
          const cachedFields = fetchedGoldFields[st.table_name] || [];
          
          if (existingFields.length === 0 && cachedFields.length === 0) {
            console.log(`Fetching fields for Gold table: ${st.table_name}`);
            try {
              const response = await api.getGoldTableColumns(bigqueryConnectionId, st.table_name);
              if (response.columns && response.columns.length > 0) {
                console.log(`Got ${response.columns.length} columns for ${st.table_name}`);
                setFetchedGoldFields(prev => ({
                  ...prev,
                  [st.table_name]: response.columns.map(c => ({
                    name: c.name || c.column_name,
                    target_field: c.name || c.column_name,
                    data_type: c.data_type
                  }))
                }));
              }
            } catch (err) {
              console.warn(`Could not fetch fields for Gold table ${st.table_name}:`, err);
            }
          }
        }
      }
    };
    
    fetchMissingGoldFields();
  }, [sourceTables, goldFieldsByTable, bigqueryConnectionId, fetchedGoldFields]);
  
  // Combine Silver and Gold tables for source selection
  const allAvailableTables = [
    ...(silverTables || []).map(t => ({ name: t, layer: 'silver', label: `[Silver] ${t}` })),
    ...(goldTables || []).map(t => ({ name: t, layer: 'gold', label: `[Gold] ${t}` }))
  ];
  
  // Build list of all available fields with table aliases
  const availableFields = [];
  console.log('GoldStageCard - sourceTables:', sourceTables);
  console.log('GoldStageCard - goldFieldsByTable keys:', Object.keys(goldFieldsByTable || {}));
  console.log('GoldStageCard - silverFieldsByTable keys:', Object.keys(silverFieldsByTable || {}));
  console.log('GoldStageCard - fetchedGoldFields keys:', Object.keys(fetchedGoldFields || {}));
  
  sourceTables.forEach(st => {
    const layer = st.source_layer || 'silver';
    let fields = [];
    
    if (layer === 'gold') {
      // First try goldFieldsByTable (computed from pipeline stages)
      fields = (goldFieldsByTable || {})[st.table_name] || [];
      // If not found, try fetchedGoldFields (from BigQuery API)
      if (fields.length === 0) {
        fields = fetchedGoldFields[st.table_name] || [];
      }
    } else {
      fields = silverFieldsByTable[st.table_name] || [];
    }
    
    console.log(`  Table ${st.table_name} (layer=${layer}): ${fields.length} fields`, fields);
    
    fields.forEach(f => {
      availableFields.push({
        value: `${st.alias}.${f.name || f.target_field}`,
        label: `${st.alias}.${f.name || f.target_field}`,
        table: st.table_name,
        alias: st.alias,
        field: f.name || f.target_field
      });
    });
  });
  
  console.log('GoldStageCard - availableFields:', availableFields.length);
  
  const addSourceTable = () => {
    const alias = `t${sourceTables.length + 1}`;
    onUpdate({
      ...stage,
      source_tables: [...sourceTables, { table_name: "", alias, role: "join", source_layer: "silver", union_fields: [] }]
    });
  };
  
  const updateSourceTable = (index, updates) => {
    const newTables = [...sourceTables];
    newTables[index] = { ...newTables[index], ...updates };
    onUpdate({ ...stage, source_tables: newTables });
  };
  
  const removeSourceTable = (index) => {
    onUpdate({ 
      ...stage, 
      source_tables: sourceTables.filter((_, i) => i !== index),
      join_conditions: joinConditions.filter(j => 
        j.left_table !== sourceTables[index]?.alias && 
        j.right_table !== sourceTables[index]?.alias
      )
    });
  };
  
  const addJoinCondition = () => {
    onUpdate({
      ...stage,
      join_conditions: [...joinConditions, { 
        left_table: sourceTables[0]?.alias || "t1", 
        left_field: "", 
        right_table: sourceTables[1]?.alias || "t2", 
        right_field: "",
        join_type: "INNER"
      }]
    });
  };
  
  const updateJoinCondition = (index, updates) => {
    const newJoins = [...joinConditions];
    newJoins[index] = { ...newJoins[index], ...updates };
    onUpdate({ ...stage, join_conditions: newJoins });
  };
  
  const removeJoinCondition = (index) => {
    onUpdate({ ...stage, join_conditions: joinConditions.filter((_, i) => i !== index) });
  };
  
  // Get fields for a specific table alias
  const getFieldsForAlias = (alias) => {
    const table = sourceTables.find(st => st.alias === alias);
    if (!table) return [];
    
    const layer = table.source_layer || 'silver';
    if (layer === 'gold') {
      // First try goldFieldsByTable (computed from pipeline stages)
      let fields = (goldFieldsByTable || {})[table.table_name] || [];
      // If not found, try fetchedGoldFields (from BigQuery API)
      if (fields.length === 0) {
        fields = fetchedGoldFields[table.table_name] || [];
      }
      return fields;
    }
    return silverFieldsByTable[table.table_name] || [];
  };
  
  const addFieldMapping = () => {
    onUpdate({
      ...stage,
      fields: [...stage.fields, { source_field: "", target_field: "", transformation: "none", data_type: "STRING" }]
    });
  };
  
  const updateField = (index, updates) => {
    const newFields = [...stage.fields];
    newFields[index] = { ...newFields[index], ...updates };
    // Auto-fill target field from source field (without alias)
    if (updates.source_field && !newFields[index].target_field) {
      const fieldName = updates.source_field.includes('.') 
        ? updates.source_field.split('.')[1] 
        : updates.source_field;
      newFields[index].target_field = fieldName;
    }
    onUpdate({ ...stage, fields: newFields });
  };
  
  const removeField = (index) => {
    onUpdate({ ...stage, fields: stage.fields.filter((_, i) => i !== index) });
  };
  
  const addCalculatedField = () => {
    onUpdate({
      ...stage,
      calculated_fields: [...(stage.calculated_fields || []), { name: "", expression: "" }]
    });
  };
  
  const updateCalculatedField = (index, updates) => {
    const newCalcFields = [...(stage.calculated_fields || [])];
    newCalcFields[index] = { ...newCalcFields[index], ...updates };
    onUpdate({ ...stage, calculated_fields: newCalcFields });
  };
  
  const removeCalculatedField = (index) => {
    onUpdate({ ...stage, calculated_fields: stage.calculated_fields.filter((_, i) => i !== index) });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="stage-card gold shadow-soft border-2 border-amber-300 bg-amber-50/30">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-amber-500 text-white">Gold Stage</Badge>
                <span className="text-sm text-muted-foreground">
                  {sourceTables.length > 0 
                    ? (() => {
                        const unionCount = sourceTables.filter(st => st.role === 'union').length;
                        const joinCount = sourceTables.filter(st => st.role !== 'union').length;
                        if (unionCount > 1 && joinCount > 0) {
                          return `Union ${unionCount} + Join ${joinCount} tables → ${stage.target_table || '?'}`;
                        } else if (unionCount > 1) {
                          return `Union ${unionCount} tables → ${stage.target_table || '?'}`;
                        } else {
                          return `Join ${sourceTables.length} tables → ${stage.target_table || '?'}`;
                        }
                      })()
                    : "Business aggregations from Silver"
                  }
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => { e.stopPropagation(); onRemove(); }}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Source Tables from Silver */}
            <div className="space-y-3">
              <Label>Source Tables (from Silver layer)</Label>
              <p className="text-xs text-muted-foreground">Add Silver or Gold tables and choose how each will be combined (Union or Join)</p>
              <div className="space-y-2">
                {sourceTables.map((st, idx) => (
                  <div key={idx} className="flex gap-2 items-center flex-wrap">
                    <Badge variant="outline" className={`w-12 justify-center font-mono ${st.source_layer === 'gold' ? 'bg-amber-50 border-amber-300' : ''}`}>{st.alias}</Badge>
                    <Select 
                      value={st.source_layer === 'gold' ? `gold:${st.table_name}` : st.table_name} 
                      onValueChange={(value) => {
                        if (value.startsWith('gold:')) {
                          const tableName = value.replace('gold:', '');
                          updateSourceTable(idx, { table_name: tableName, source_layer: 'gold' });
                        } else {
                          updateSourceTable(idx, { table_name: value, source_layer: 'silver' });
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1 min-w-[200px]">
                        <SelectValue placeholder="Select table" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-50">Silver Layer</div>
                        {silverTables.map((table) => (
                          <SelectItem key={`silver_${table}`} value={table}>
                            silver_layer.{table}
                          </SelectItem>
                        ))}
                        {goldTables && goldTables.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-amber-50 mt-1">Gold Layer</div>
                            {goldTables.map((table) => (
                              <SelectItem key={`gold_${table}`} value={`gold:${table}`}>
                                gold_layer.{table}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={st.role || "join"} 
                      onValueChange={(value) => updateSourceTable(idx, { role: value })}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="join">
                          <span className="flex items-center gap-1">
                            <Link2 className="w-3 h-3" /> Join
                          </span>
                        </SelectItem>
                        <SelectItem value="union">
                          <span className="flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Union
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Union Fields Button - Only show for union tables */}
                    {st.role === 'union' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowUnionFieldsModal(idx)}
                        className="text-xs"
                        title="Configure per-table field mappings for UNION"
                      >
                        <Settings className="w-3 h-3 mr-1" />
                        Fields ({(st.union_fields || []).length})
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => removeSourceTable(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addSourceTable}>
                  <Plus className="w-4 h-4 mr-1" /> Add Table
                </Button>
              </div>
            </div>
            
            {/* Flexible Combine Mode Info - Only show when multiple tables */}
            {sourceTables.length > 1 && (() => {
              const unionTables = sourceTables.filter(st => st.role === 'union');
              const joinTables = sourceTables.filter(st => st.role !== 'union');
              const hasUnion = unionTables.length > 1;
              const hasJoin = joinTables.length > 0;
              const hasBoth = hasUnion && hasJoin;
              
              return (
                <div className="space-y-3 bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                  <Label className="flex items-center gap-2 font-medium">
                    <Link2 className="w-4 h-4 text-amber-600" /> Combine Summary
                  </Label>
                  
                  {/* Dynamic summary based on table roles */}
                  <div className="text-sm space-y-2">
                    {unionTables.length > 1 && (
                      <div className="flex items-center gap-2 bg-blue-50 p-2 rounded border border-blue-200">
                        <Badge className="bg-blue-500">UNION ALL</Badge>
                        <span className="text-blue-800">
                          {unionTables.map(t => t.alias).join(' + ')} 
                          <span className="text-blue-600 ml-1">({unionTables.length} tables stacked)</span>
                        </span>
                      </div>
                    )}
                    {unionTables.length === 1 && (
                      <div className="flex items-center gap-2 bg-yellow-50 p-2 rounded border border-yellow-200">
                        <Badge variant="outline" className="text-yellow-700 border-yellow-400">⚠️</Badge>
                        <span className="text-yellow-800 text-xs">
                          {unionTables[0].alias} is marked as Union but needs at least 2 tables. Add another Union table or change to Join.
                        </span>
                      </div>
                    )}
                    {hasBoth && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>↓ then</span>
                      </div>
                    )}
                    {joinTables.length > 0 && (
                      <div className="flex items-center gap-2 bg-green-50 p-2 rounded border border-green-200">
                        <Badge className="bg-green-500">JOIN</Badge>
                        <span className="text-green-800">
                          {hasBoth ? 'Union result' : joinTables[0]?.alias}
                          {joinTables.slice(hasBoth ? 0 : 1).map(t => ` ⟕ ${t.alias}`).join('')}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {hasBoth && (
                    <p className="text-xs text-amber-700 bg-amber-100 p-2 rounded">
                      💡 <strong>Flexible Mode:</strong> Tables marked &quot;Union&quot; will be stacked first (UNION ALL), 
                      then the result will be joined with tables marked &quot;Join&quot;.
                    </p>
                  )}
                  {!hasUnion && joinTables.length > 1 && (
                    <p className="text-xs text-green-700 bg-green-100 p-2 rounded">
                      💡 <strong>Join Mode:</strong> All tables will be joined together using the conditions below.
                    </p>
                  )}
                  {hasUnion && !hasJoin && (
                    <p className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                      💡 <strong>Union Mode:</strong> All Union tables will be stacked vertically (UNION ALL). 
                      Best for tables with similar structures.
                    </p>
                  )}
                </div>
              );
            })()}
            
            {/* Join Conditions - Show when there are tables to join */}
            {sourceTables.length > 1 && sourceTables.filter(st => st.role !== 'union').length > 0 && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" /> Join Conditions
                </Label>
                <p className="text-xs text-muted-foreground">Define how to join the tables together</p>
                <div className="space-y-2">
                  {joinConditions.map((join, idx) => {
                    const leftFields = getFieldsForAlias(join.left_table);
                    const rightFields = getFieldsForAlias(join.right_table);
                    return (
                      <div key={idx} className="flex gap-2 items-center bg-white p-2 rounded border flex-wrap">
                        <Select value={join.join_type} onValueChange={(v) => updateJoinCondition(idx, { join_type: v })}>
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {JOIN_TYPES.map((j) => (
                              <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={join.left_table} onValueChange={(v) => updateJoinCondition(idx, { left_table: v, left_field: "" })}>
                          <SelectTrigger className="w-16">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sourceTables.map((st) => (
                              <SelectItem key={st.alias} value={st.alias}>{st.alias}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={join.left_field} 
                          onValueChange={(v) => updateJoinCondition(idx, { left_field: v })}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue placeholder="Select field" />
                          </SelectTrigger>
                          <SelectContent>
                            {leftFields.map((f) => (
                              <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-muted-foreground font-bold">=</span>
                        <Select value={join.right_table} onValueChange={(v) => updateJoinCondition(idx, { right_table: v, right_field: "" })}>
                          <SelectTrigger className="w-16">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sourceTables.map((st) => (
                              <SelectItem key={st.alias} value={st.alias}>{st.alias}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={join.right_field} 
                          onValueChange={(v) => updateJoinCondition(idx, { right_field: v })}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue placeholder="Select field" />
                          </SelectTrigger>
                          <SelectContent>
                            {rightFields.map((f) => (
                              <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" onClick={() => removeJoinCondition(idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                  <Button variant="outline" size="sm" onClick={addJoinCondition}>
                    <Plus className="w-4 h-4 mr-1" /> Add Join
                  </Button>
                </div>
              </div>
            )}
            
            {/* Target Table Name */}
            <div className="space-y-2">
              <Label>Target Table Name (gold_layer)</Label>
              <div className="flex gap-2">
                <Input
                  className="flex-1"
                  value={stage.target_table || ""}
                  onChange={(e) => onUpdate({ ...stage, target_table: e.target.value })}
                  placeholder="e.g., sales_summary"
                />
                {stage.target_table && bigqueryConnectionId && (
                  <DataPreviewDialog
                    type="gold"
                    bigqueryConnectionId={bigqueryConnectionId}
                    tableName={stage.target_table}
                  />
                )}
              </div>
            </div>
            
            {/* Field Mappings */}
            <div className="space-y-3">
              <Label>Field Mappings {sourceTables.filter(st => st.role === 'union').length > 1 && <Badge variant="outline" className="ml-2 text-xs">Common Fields for Union Tables</Badge>}</Label>
              {sourceTables.length === 0 ? (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠️ Add source tables first to see available fields.
                </p>
              ) : availableFields.length === 0 ? (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠️ No fields found in selected Silver tables. Make sure Silver stages have field mappings defined.
                </p>
              ) : sourceTables.filter(st => st.role === 'union').length > 1 ? (
                <p className="text-xs text-muted-foreground">
                  Select fields to include. Union tables will share common columns; Join tables will be matched by conditions.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Select fields from your Silver tables. Use alias.field format (e.g., t1.customer_id).
                </p>
              )}
              <div className="space-y-2">
                {stage.fields.map((field, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white p-2 rounded border">
                    <Select 
                      value={field.source_field || ""} 
                      onValueChange={(value) => updateField(idx, { source_field: value })}
                    >
                      <SelectTrigger className="col-span-4">
                        <SelectValue placeholder="Select field (t1.field)" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFields.map((f) => (
                          <SelectItem key={f.value} value={f.value}>
                            <span className="font-mono text-xs">{f.value}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <ArrowRight className="col-span-1 w-4 h-4 text-muted-foreground mx-auto" />
                    <Input
                      className="col-span-2"
                      value={field.target_field}
                      onChange={(e) => updateField(idx, { target_field: e.target.value })}
                      placeholder="Target name"
                    />
                    <Select 
                      value={field.data_type || "STRING"} 
                      onValueChange={(value) => updateField(idx, { data_type: value })}
                    >
                      <SelectTrigger className="col-span-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATA_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={field.transformation || "none"} 
                      onValueChange={(value) => updateField(idx, { transformation: value })}
                    >
                      <SelectTrigger className="col-span-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRANSFORMATIONS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeField(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addFieldMapping} disabled={sourceTables.length === 0}>
                  <Plus className="w-4 h-4 mr-1" /> Add Field
                </Button>
              </div>
            </div>
            
            {/* Calculated Fields */}
            <div className="space-y-3">
              <Label>Calculated Fields (Optional)</Label>
              <p className="text-xs text-muted-foreground">
                Create new fields using SQL expressions. Use t1.field, t2.field format.
              </p>
              <div className="space-y-2">
                {(stage.calculated_fields || []).map((calc, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white p-2 rounded border">
                    <Input
                      className="col-span-3"
                      value={calc.name}
                      onChange={(e) => updateCalculatedField(idx, { name: e.target.value })}
                      placeholder="Field name"
                    />
                    <span className="col-span-1 text-center text-muted-foreground">=</span>
                    <Input
                      className="col-span-7 font-mono text-sm"
                      value={calc.expression}
                      onChange={(e) => updateCalculatedField(idx, { expression: e.target.value })}
                      placeholder="e.g., t1.price * t1.quantity"
                    />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeCalculatedField(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addCalculatedField}>
                  <Plus className="w-4 h-4 mr-1" /> Add Calculation
                </Button>
              </div>
            </div>
            
            {/* Filter */}
            <div className="space-y-2">
              <Label>Filter (WHERE clause)</Label>
              <Input
                value={stage.filter_condition || ""}
                onChange={(e) => onUpdate({ ...stage, filter_condition: e.target.value })}
                placeholder="e.g., t1.status = 'ACTIVE'"
                className="font-mono text-sm"
              />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
      
      {/* Union Fields Modal - Per-table field mapping for UNION mode */}
      {showUnionFieldsModal !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[800px] max-h-[80vh] overflow-auto">
            <div className="p-4 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-lg">
                    Configure Union Fields for {sourceTables[showUnionFieldsModal]?.alias} ({sourceTables[showUnionFieldsModal]?.table_name})
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Define field mappings for this table in the UNION. Use expressions, literals, or NULL.
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowUnionFieldsModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Field Mapping Help */}
              <div className="bg-blue-50 p-3 rounded-lg text-sm space-y-2">
                <p className="font-medium text-blue-800">Expression Examples:</p>
                <ul className="text-blue-700 space-y-1 ml-4 list-disc">
                  <li><code className="bg-blue-100 px-1 rounded">quantity_sold</code> - Simple field reference</li>
                  <li><code className="bg-blue-100 px-1 rounded">quantity_returned * -1</code> - Negate values (for returns)</li>
                  <li><code className="bg-blue-100 px-1 rounded">&apos;Sale&apos;</code> - Literal string value</li>
                  <li><code className="bg-blue-100 px-1 rounded">NULL</code> - NULL placeholder for missing fields</li>
                  <li><code className="bg-blue-100 px-1 rounded">CAST(field AS STRING)</code> - Type conversion</li>
                  <li><code className="bg-blue-100 px-1 rounded">COALESCE(field1, field2)</code> - First non-null value</li>
                </ul>
              </div>
              
              {/* Field Mapping List */}
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2">
                  <span className="col-span-5">Source Expression</span>
                  <span className="col-span-4">Target Field</span>
                  <span className="col-span-2">Data Type</span>
                  <span className="col-span-1"></span>
                </div>
                
                {(sourceTables[showUnionFieldsModal]?.union_fields || []).map((field, fIdx) => (
                  <div key={fIdx} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded">
                    <Input
                      className="col-span-5 font-mono text-sm"
                      value={field.source_expression || ''}
                      onChange={(e) => {
                        const newTables = [...sourceTables];
                        const newFields = [...(newTables[showUnionFieldsModal].union_fields || [])];
                        newFields[fIdx] = { ...newFields[fIdx], source_expression: e.target.value };
                        newTables[showUnionFieldsModal] = { ...newTables[showUnionFieldsModal], union_fields: newFields };
                        onUpdate({ ...stage, source_tables: newTables });
                      }}
                      placeholder="field_name or expression"
                    />
                    <Input
                      className="col-span-4"
                      value={field.target_field || ''}
                      onChange={(e) => {
                        const newTables = [...sourceTables];
                        const newFields = [...(newTables[showUnionFieldsModal].union_fields || [])];
                        newFields[fIdx] = { ...newFields[fIdx], target_field: e.target.value };
                        newTables[showUnionFieldsModal] = { ...newTables[showUnionFieldsModal], union_fields: newFields };
                        onUpdate({ ...stage, source_tables: newTables });
                      }}
                      placeholder="target_column"
                    />
                    <Select 
                      value={field.data_type || "STRING"} 
                      onValueChange={(value) => {
                        const newTables = [...sourceTables];
                        const newFields = [...(newTables[showUnionFieldsModal].union_fields || [])];
                        newFields[fIdx] = { ...newFields[fIdx], data_type: value };
                        newTables[showUnionFieldsModal] = { ...newTables[showUnionFieldsModal], union_fields: newFields };
                        onUpdate({ ...stage, source_tables: newTables });
                      }}
                    >
                      <SelectTrigger className="col-span-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATA_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="col-span-1"
                      onClick={() => {
                        const newTables = [...sourceTables];
                        const newFields = (newTables[showUnionFieldsModal].union_fields || []).filter((_, i) => i !== fIdx);
                        newTables[showUnionFieldsModal] = { ...newTables[showUnionFieldsModal], union_fields: newFields };
                        onUpdate({ ...stage, source_tables: newTables });
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    const newTables = [...sourceTables];
                    const newFields = [...(newTables[showUnionFieldsModal].union_fields || []), { source_expression: '', target_field: '', data_type: 'STRING' }];
                    newTables[showUnionFieldsModal] = { ...newTables[showUnionFieldsModal], union_fields: newFields };
                    onUpdate({ ...stage, source_tables: newTables });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Field Mapping
                </Button>
              </div>
            </div>
            
            <div className="p-4 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setShowUnionFieldsModal(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </Collapsible>
  );
});

// Add display name for debugging
GoldStageCard.displayName = 'GoldStageCard';

export default function PipelineBuilder() {
  const navigate = useNavigate();
  const { id: pipelineId } = useParams();
  const isEditing = !!pipelineId;
  
  const [pipelineName, setPipelineName] = useState("");
  const [sourceType, setSourceType] = useState("oracle"); // oracle, sqlserver, postgresql
  const [sourceConnectionId, setSourceConnectionId] = useState("");
  const [oracleConnectionId, setOracleConnectionId] = useState(""); // Legacy support
  const [bigqueryConnectionId, setBigqueryConnectionId] = useState("");
  const [stages, setStages] = useState([]);
  const [dataStrategy, setDataStrategy] = useState("override"); // override or merge
  
  const [oracleConnections, setOracleConnections] = useState([]);
  const [sqlserverConnections, setSqlserverConnections] = useState([]);
  const [postgresqlConnections, setPostgresqlConnections] = useState([]);
  const [bigqueryConnections, setBigqueryConnections] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [columnsByTable, setColumnsByTable] = useState({});
  const [loadingColumns, setLoadingColumns] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // AI Gold Generation state
  const [showAIGoldModal, setShowAIGoldModal] = useState(false);
  const [aiApiKey, setAiApiKey] = useState(() => localStorage.getItem("openai_api_key") || "");
  const [aiUseCase, setAiUseCase] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGoldSuggestion, setAiGoldSuggestion] = useState(null);
  const [aiGoldStages, setAiGoldStages] = useState([]); // NEW: For multi-stage Gold
  const [aiIsMultiStage, setAiIsMultiStage] = useState(false); // NEW: Flag for multi-stage
  const [aiFeedback, setAiFeedback] = useState("");
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiSelectedTables, setAiSelectedTables] = useState([]); // Selected Silver tables for AI

  // SQL Import state
  const [showSQLImportModal, setShowSQLImportModal] = useState(false);
  const [existingBigQueryGoldTables, setExistingBigQueryGoldTables] = useState([]);
  const [useNewGoldUI, setUseNewGoldUI] = useState(true); // Toggle for new vs old Gold UI

  // SQL Export state
  const [showSQLExportModal, setShowSQLExportModal] = useState(false);
  const [exportingSQLLoading, setExportingSQLLoading] = useState(false);
  const [exportedSQL, setExportedSQL] = useState(null);

  // Get currently selected source connections based on type
  const currentSourceConnections = sourceType === 'oracle' 
    ? oracleConnections 
    : sourceType === 'sqlserver' 
    ? sqlserverConnections 
    : postgresqlConnections;

  // Get bronze and silver table names for dropdowns
  const bronzeTables = stages
    .filter(s => s.stage_type === "bronze" && s.target_table)
    .map(s => s.target_table);
  
  // Map bronze table names to their selected fields (for Silver stage field selection)
  const bronzeFieldsByTable = {};
  stages
    .filter(s => s.stage_type === "bronze" && s.target_table && s.fields.length > 0)
    .forEach(s => {
      bronzeFieldsByTable[s.target_table] = s.fields.map(f => ({
        name: f.target_field || f.source_field,
        type: "STRING" // Bronze stores everything as STRING
      }));
    });
  
  const silverTables = stages
    .filter(s => s.stage_type === "silver" && s.target_table)
    .map(s => s.target_table);
  
  // Map silver table names to their selected fields (for Gold stage field selection)
  const silverFieldsByTable = {};
  stages
    .filter(s => s.stage_type === "silver" && s.target_table && s.fields.length > 0)
    .forEach(s => {
      silverFieldsByTable[s.target_table] = s.fields.map(f => ({
        name: f.target_field || f.source_field,
        type: f.data_type || "STRING"
      }));
    });

   
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch existing BigQuery Gold tables when connection is selected
  useEffect(() => {
    const fetchBigQueryGoldTables = async () => {
      if (bigqueryConnectionId) {
        try {
          const response = await api.getBigQueryGoldTables(bigqueryConnectionId);
          const tableNames = (response.tables || []).map(t => t.table_name);
          setExistingBigQueryGoldTables(tableNames);
          console.log('Fetched BigQuery Gold tables:', tableNames);
        } catch (err) {
          console.warn('Could not fetch BigQuery Gold tables:', err);
          setExistingBigQueryGoldTables([]);
        }
      }
    };
    fetchBigQueryGoldTables();
  }, [bigqueryConnectionId]);
   
  useEffect(() => {
    if (sourceConnectionId) {
      fetchSchemas();
    }
  }, [sourceConnectionId, sourceType]);

  const fetchInitialData = async () => {
    try {
      const [oracle, sqlserver, postgresql, bigquery] = await Promise.all([
        api.getOracleConnections(),
        api.getSQLServerConnections(),
        api.getPostgreSQLConnections(),
        api.getBigQueryConnections()
      ]);
      
      // When editing, store ALL connections (including disconnected) for reference
      // When creating new, only show connected ones
      if (isEditing) {
        // For editing, include all connections so user can see which one was used
        setOracleConnections(oracle);
        setSqlserverConnections(sqlserver);
        setPostgresqlConnections(postgresql);
        setBigqueryConnections(bigquery);
      } else {
        // For new pipelines, only show connected connections
        setOracleConnections(oracle.filter(c => c.status === "connected"));
        setSqlserverConnections(sqlserver.filter(c => c.status === "connected"));
        setPostgresqlConnections(postgresql.filter(c => c.status === "connected"));
        setBigqueryConnections(bigquery.filter(c => c.status === "connected"));
      }
      
      if (isEditing) {
        const pipeline = await api.getPipeline(pipelineId);
        setPipelineName(pipeline.name);
        // Support both new and legacy fields
        const pipelineSourceType = pipeline.source_type || 'oracle';
        const pipelineConnectionId = pipeline.source_connection_id || pipeline.oracle_connection_id;
        setSourceType(pipelineSourceType);
        setSourceConnectionId(pipelineConnectionId);
        setOracleConnectionId(pipeline.oracle_connection_id || '');
        setBigqueryConnectionId(pipeline.bigquery_connection_id);
        setDataStrategy(pipeline.data_strategy || 'override');
        
        // Build a case-insensitive map of bronze target tables for Silver stage normalization
        const bronzeTargetMap = {};
        pipeline.stages.forEach(s => {
          if (s.stage_type === 'bronze' && s.target_table) {
            bronzeTargetMap[s.target_table.toLowerCase()] = s.target_table;
          }
        });
        
        // Normalize stages for consistent data format
        const normalizedStages = pipeline.stages.map(stage => {
          // Normalize Gold stage source_tables if they're in string format
          if (stage.stage_type === 'gold' && stage.source_tables) {
            const normalizedSourceTables = stage.source_tables.map((table, idx) => {
              if (typeof table === 'string') {
                return { table_name: table, alias: `t${idx + 1}`, role: 'join', source_layer: 'silver', union_fields: [] };
              }
              // Preserve all existing properties including source_layer, role, union_fields
              return { 
                table_name: table.table_name || table, 
                alias: table.alias || `t${idx + 1}`,
                role: table.role || 'join',
                source_layer: table.source_layer || 'silver',
                union_fields: table.union_fields || []
              };
            });
            return { ...stage, source_tables: normalizedSourceTables };
          }
          
          // Normalize Silver stage source_table to match Bronze target_table (case-insensitive fix)
          if (stage.stage_type === 'silver' && stage.source_table) {
            const normalizedSourceTable = bronzeTargetMap[stage.source_table.toLowerCase()] || stage.source_table;
            if (normalizedSourceTable !== stage.source_table) {
              console.log(`Normalized Silver source_table: "${stage.source_table}" -> "${normalizedSourceTable}"`);
              return { ...stage, source_table: normalizedSourceTable };
            }
          }
          
          return stage;
        });
        setStages(normalizedStages);
        
        // IMPORTANT: Set loading to false FIRST so UI shows immediately
        // Then fetch columns in background (non-blocking)
        setLoading(false);
        
        // Fetch columns for existing Bronze stages IN PARALLEL (background)
        if (pipelineConnectionId && pipeline.stages) {
          const bronzeStages = pipeline.stages.filter(s => s.stage_type === 'bronze' && s.source_table && s.source_schema);
          const uniqueTables = {};
          
          for (const stage of bronzeStages) {
            const key = `${stage.source_schema}.${stage.source_table}`;
            if (!uniqueTables[key]) {
              uniqueTables[key] = { schema: stage.source_schema, table: stage.source_table };
            }
          }
          
          // Fetch ALL columns in PARALLEL - don't wait for each one
          const fetchPromises = Object.entries(uniqueTables).map(async ([key, { schema, table }]) => {
            try {
              let data;
              if (pipelineSourceType === 'oracle') {
                data = await api.getOracleColumns(pipelineConnectionId, table, schema);
              } else if (pipelineSourceType === 'sqlserver') {
                data = await api.getSQLServerColumns(pipelineConnectionId, schema, table);
              } else if (pipelineSourceType === 'postgresql') {
                data = await api.getPostgreSQLColumns(pipelineConnectionId, schema, table);
              }
              if (data?.columns) {
                return { key, columns: data.columns };
              }
            } catch (error) {
              console.error(`Failed to load columns for ${key}:`, error);
            }
            return null;
          });
          
          // Execute all fetches in parallel
          Promise.all(fetchPromises).then(results => {
            const newColumns = {};
            results.forEach(result => {
              if (result) {
                newColumns[result.key] = result.columns;
              }
            });
            if (Object.keys(newColumns).length > 0) {
              setColumnsByTable(prev => ({ ...prev, ...newColumns }));
            }
          });
        }
        
        return; // Exit early since we already set loading to false
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSchemas = async () => {
    try {
      let data;
      if (sourceType === 'oracle') {
        data = await api.getOracleSchemas(sourceConnectionId);
        setSchemas(data.schemas || []);
      } else if (sourceType === 'sqlserver') {
        data = await api.getSQLServerSchemas(sourceConnectionId);
        setSchemas(data.schemas || []);
      } else if (sourceType === 'postgresql') {
        data = await api.getPostgreSQLSchemas(sourceConnectionId);
        setSchemas(data.schemas || []);
      }
    } catch (error) {
      toast.error("Failed to load schemas");
      setSchemas([]);
    }
  };

  const fetchColumns = async (tableName, schema) => {
    if (!sourceConnectionId) return;
    const key = `${schema}.${tableName}`;
    if (columnsByTable[key]) return;
    
    setLoadingColumns(true);
    try {
      let data;
      if (sourceType === 'oracle') {
        data = await api.getOracleColumns(sourceConnectionId, tableName, schema);
      } else if (sourceType === 'sqlserver') {
        data = await api.getSQLServerColumns(sourceConnectionId, schema, tableName);
      } else if (sourceType === 'postgresql') {
        data = await api.getPostgreSQLColumns(sourceConnectionId, schema, tableName);
      }
      setColumnsByTable(prev => ({ ...prev, [key]: data.columns || [] }));
    } catch (error) {
      toast.error("Failed to load columns");
    } finally {
      setLoadingColumns(false);
    }
  };

  const addStage = (stageType) => {
    const newStage = {
      stage_type: stageType,
      source_schema: "",
      source_table: "",
      source_tables: stageType === "gold" ? [] : undefined,
      join_conditions: stageType === "gold" ? [] : undefined,
      target_table: "",
      fields: [],
      calculated_fields: [],
      filter_condition: ""
    };
    setStages([...stages, newStage]);
  };

  // Use useCallback to prevent recreating this function on every render
  const updateStage = useCallback((index, updatedStage) => {
    setStages(prevStages => {
      const newStages = [...prevStages];
      newStages[index] = updatedStage;
      return newStages;
    });
  }, []);

  const removeStage = useCallback((index) => {
    setStages(prevStages => prevStages.filter((_, i) => i !== index));
  }, []);

  const addMultipleBronzeStages = (schema, tableNames) => {
    // Create a Bronze stage for each selected table
    const newStages = tableNames.map(tableName => ({
      stage_type: "bronze",
      source_schema: schema,
      source_table: tableName,
      target_table: `raw_${tableName.toLowerCase()}`,
      fields: [], // Will select all fields by default when executed
      calculated_fields: [],
      filter_condition: ""
    }));
    
    setStages([...stages, ...newStages]);
    toast.success(`Added ${tableNames.length} Bronze stage${tableNames.length > 1 ? 's' : ''}`);
  };

  // AI Gold Generation Functions
  const generateGoldWithAI = async () => {
    if (!aiApiKey.trim()) {
      toast.error("Please enter your OpenAI API key");
      return;
    }
    if (!aiUseCase.trim()) {
      toast.error("Please describe your use case");
      return;
    }
    
    // Get silver stages for context - filter by selected tables if any
    let silverStages = stages.filter(s => s.stage_type === "silver");
    if (silverStages.length === 0) {
      toast.error("Please create at least one Silver stage first");
      return;
    }
    
    // If user selected specific tables, filter to only those
    if (aiSelectedTables.length > 0) {
      silverStages = silverStages.filter(s => aiSelectedTables.includes(s.target_table));
      if (silverStages.length === 0) {
        toast.error("No Silver stages match your selected tables");
        return;
      }
    }
    
    setAiGenerating(true);
    try {
      // Save API key
      localStorage.setItem("openai_api_key", aiApiKey);
      
      const response = await api.generateGoldStage({
        use_case: aiUseCase,
        api_key: aiApiKey,
        silver_tables: silverStages,
        feedback: aiFeedback || null,
        session_id: aiSessionId
      });
      
      if (response.success) {
        if (response.is_multi_stage && response.gold_stages) {
          // Multi-stage response
          setAiIsMultiStage(true);
          setAiGoldStages(response.gold_stages);
          setAiGoldSuggestion(null);
          setAiSessionId(response.session_id);
          setAiFeedback("");
          toast.success(`Generated ${response.gold_stages.length} Gold stages!`);
        } else if (response.gold_stage) {
          // Single-stage response (legacy)
          setAiIsMultiStage(false);
          setAiGoldStages([]);
          setAiGoldSuggestion(response.gold_stage);
          setAiSessionId(response.session_id);
          setAiFeedback("");
          toast.success("Gold stage suggestion generated!");
        } else {
          toast.error("Failed to generate Gold stage: Invalid response format");
          console.log("Raw AI response:", response.raw_response);
        }
      } else {
        toast.error("Failed to generate Gold stage: " + (response.error || "Unknown error"));
        console.log("Raw AI response:", response.raw_response);
      }
    } catch (error) {
      console.error("AI generation error:", error);
      // Extract error message from API response if available
      const errorMessage = error.response?.data?.detail || error.message || "Unknown error";
      toast.error("Failed to generate Gold stage: " + errorMessage);
    } finally {
      setAiGenerating(false);
    }
  };

  const approveGoldSuggestion = () => {
    // Handle multi-stage approval
    if (aiIsMultiStage && aiGoldStages.length > 0) {
      const newGoldStages = aiGoldStages.map((stageSuggestion) => {
        const rawSourceTables = stageSuggestion.source_tables || [];
        const convertedSourceTables = rawSourceTables.map((table, idx) => {
          if (typeof table === 'string') {
            return { table_name: table, alias: `t${idx + 1}`, role: 'join', source_layer: 'silver', union_fields: [] };
          }
          return { 
            table_name: table.table_name || table, 
            alias: table.alias || `t${idx + 1}`,
            role: table.role || 'join',
            source_layer: table.source_layer || 'silver',
            union_fields: table.union_fields || []
          };
        });
        
        const tableToAlias = {};
        convertedSourceTables.forEach(st => {
          tableToAlias[st.table_name] = st.alias;
        });
        
        const rawJoins = stageSuggestion.join_conditions || stageSuggestion.joins || [];
        const convertedJoins = rawJoins.map(join => ({
          ...join,
          left_table: tableToAlias[join.left_table] || join.left_table,
          right_table: tableToAlias[join.right_table] || join.right_table,
          join_type: join.join_type || 'LEFT'
        }));
        
        const convertedFields = (stageSuggestion.fields || []).map(f => {
          let sourceField = f.source_field;
          if (sourceField && sourceField.includes('.')) {
            const [tablePart, fieldPart] = sourceField.split('.');
            const alias = tableToAlias[tablePart] || tablePart;
            sourceField = `${alias}.${fieldPart}`;
          }
          return {
            source_field: sourceField,
            target_field: f.target_field,
            data_type: f.data_type || "STRING",
            transformation: f.transformation || "none"
          };
        });
        
        const convertedCalculatedFields = (stageSuggestion.calculated_fields || []).map(calc => ({
          name: calc.name,
          expression: calc.expression
        }));
        
        return {
          stage_type: "gold",
          source_tables: convertedSourceTables,
          target_table: stageSuggestion.target_table || "gold_analytics",
          join_conditions: convertedJoins,
          fields: convertedFields,
          calculated_fields: convertedCalculatedFields,
          filter_condition: stageSuggestion.filter_condition || ""
        };
      });
      
      setStages([...stages, ...newGoldStages]);
      setShowAIGoldModal(false);
      setAiGoldStages([]);
      setAiGoldSuggestion(null);
      setAiIsMultiStage(false);
      setAiUseCase("");
      setAiFeedback("");
      toast.success(`Added ${newGoldStages.length} Gold stages to pipeline!`);
      return;
    }
    
    // Handle single-stage approval (legacy)
    if (!aiGoldSuggestion) return;
    
    // Convert AI source_tables (string array) to proper format (object array with aliases and roles)
    const rawSourceTables = aiGoldSuggestion.source_tables || [];
    const convertedSourceTables = rawSourceTables.map((table, idx) => {
      // Handle both string format and object format
      if (typeof table === 'string') {
        return { table_name: table, alias: `t${idx + 1}`, role: 'join', source_layer: 'silver', union_fields: [] };
      }
      return { 
        table_name: table.table_name || table, 
        alias: table.alias || `t${idx + 1}`,
        role: table.role || 'join',
        source_layer: table.source_layer || 'silver',
        union_fields: table.union_fields || []
      };
    });
    
    // Build table name to alias mapping
    const tableToAlias = {};
    convertedSourceTables.forEach(st => {
      tableToAlias[st.table_name] = st.alias;
    });
    
    // Convert joins/join_conditions to use aliases - handle both "joins" and "join_conditions" keys
    const rawJoins = aiGoldSuggestion.join_conditions || aiGoldSuggestion.joins || [];
    const convertedJoins = rawJoins.map(join => ({
      ...join,
      left_table: tableToAlias[join.left_table] || join.left_table,
      right_table: tableToAlias[join.right_table] || join.right_table,
      join_type: join.join_type || 'LEFT'
    }));
    
    // Convert fields to use alias.field format
    const convertedFields = (aiGoldSuggestion.fields || []).map(f => {
      let sourceField = f.source_field;
      // If source_field contains a table name (not alias), convert it
      if (sourceField && sourceField.includes('.')) {
        const [tablePart, fieldPart] = sourceField.split('.');
        const alias = tableToAlias[tablePart] || tablePart;
        sourceField = `${alias}.${fieldPart}`;
      }
      return {
        source_field: sourceField,
        target_field: f.target_field,
        data_type: f.data_type || "STRING",
        transformation: f.transformation || "none"
      };
    });
    
    // Convert calculated_fields
    const convertedCalculatedFields = (aiGoldSuggestion.calculated_fields || []).map(calc => ({
      name: calc.name,
      expression: calc.expression
    }));
    
    // Convert AI suggestion to Gold stage format
    const goldStage = {
      stage_type: "gold",
      source_tables: convertedSourceTables,
      target_table: aiGoldSuggestion.target_table || "gold_analytics",
      join_conditions: convertedJoins,
      fields: convertedFields,
      calculated_fields: convertedCalculatedFields,
      filter_condition: aiGoldSuggestion.filter_condition || ""
    };
    
    setStages([...stages, goldStage]);
    setShowAIGoldModal(false);
    setAiGoldSuggestion(null);
    setAiUseCase("");
    setAiFeedback("");
    
    // Check if AI suggested any union tables
    const unionCount = convertedSourceTables.filter(t => t.role === 'union').length;
    const joinCount = convertedSourceTables.filter(t => t.role === 'join').length;
    
    if (unionCount > 0 && joinCount > 0) {
      toast.success(`Gold stage "${goldStage.target_table}" added with flexible mode (${unionCount} union + ${joinCount} join tables)!`);
    } else if (unionCount > 1) {
      toast.success(`Gold stage "${goldStage.target_table}" added with UNION ALL mode (${unionCount} tables)!`);
    } else {
      toast.success(`Gold stage "${goldStage.target_table}" added to pipeline!`);
    }
  };

  const rejectGoldSuggestion = () => {
    if (!aiFeedback.trim()) {
      toast.error("Please provide feedback on what to change");
      return;
    }
    // Keep the session ID for context, regenerate with feedback
    setAiGoldSuggestion(null);
    generateGoldWithAI();
  };

  const handleSave = async () => {
    if (!pipelineName.trim()) {
      toast.error("Please enter a pipeline name");
      return;
    }
    if (!sourceConnectionId || !bigqueryConnectionId) {
      toast.error("Please select both source and destination connections");
      return;
    }
    if (stages.length === 0) {
      toast.error("Please add at least one stage");
      return;
    }
    
    setSaving(true);
    try {
      // Process stages to convert visual filters/mappings to SQL
      const processedStages = stages.map(stage => {
        if (stage.stage_type === 'silver') {
          // Convert visual filters to SQL filter_condition
          const filterSQL = convertVisualFiltersToSQL(stage.visual_filters);
          
          // Convert value mappings to additional CASE expression fields
          const mappingFields = convertValueMappingsToFields(stage.value_mappings);
          
          // Combine regular fields with mapping fields
          const allFields = [...(stage.fields || []), ...mappingFields];
          
          return {
            ...stage,
            filter_condition: filterSQL || stage.filter_condition || "",
            fields: allFields,
            // Keep visual_filters and value_mappings for UI editing later
            visual_filters: stage.visual_filters,
            value_mappings: stage.value_mappings
          };
        }
        return stage;
      });
      
      const pipelineData = {
        name: pipelineName,
        source_type: sourceType,
        source_connection_id: sourceConnectionId,
        oracle_connection_id: sourceType === 'oracle' ? sourceConnectionId : null, // Legacy support
        bigquery_connection_id: bigqueryConnectionId,
        data_strategy: dataStrategy,
        stages: processedStages
        // Note: tenant_id is NOT stored in pipeline - it's selected at runtime
      };
      
      if (isEditing) {
        // Update existing pipeline (last-write-wins)
        await api.updatePipeline(pipelineId, pipelineData);
        toast.success("Pipeline updated successfully!");
      } else {
        // Create new pipeline
        await api.createPipeline(pipelineData);
        toast.success("Pipeline created! Select a tenant when you run it.");
      }
      navigate("/pipelines");
    } catch (error) {
      toast.error(isEditing ? "Failed to update pipeline" : "Failed to save pipeline");
    } finally {
      setSaving(false);
    }
  };

  // Export SQL for all stages
  const handleExportSQL = async () => {
    if (!isEditing) {
      toast.error("Please save the pipeline first before exporting SQL");
      return;
    }

    setExportingSQLLoading(true);
    setShowSQLExportModal(true);
    
    try {
      const data = await api.exportPipelineSQL(pipelineId);
      setExportedSQL(data);
    } catch (error) {
      toast.error("Failed to export SQL");
      console.error(error);
      setShowSQLExportModal(false);
    } finally {
      setExportingSQLLoading(false);
    }
  };

  // Copy SQL to clipboard
  const handleCopySQL = (sql) => {
    navigator.clipboard.writeText(sql);
    toast.success("SQL copied to clipboard!");
  };

  // Download SQL as file
  const handleDownloadSQL = (stageName, sql) => {
    const blob = new Blob([sql], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pipelineName}_${stageName.replace(/\s+/g, '_')}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("SQL downloaded!");
  };

  // Download all stages SQL
  const handleDownloadAllSQL = () => {
    if (!exportedSQL || !exportedSQL.stages) return;
    
    let allSQL = `-- ============================================
-- PIPELINE: ${exportedSQL.pipeline_name}
-- Exported: ${new Date(exportedSQL.exported_at).toLocaleString()}
-- BigQuery Project: ${exportedSQL.bq_project}
-- ============================================\n\n`;
    
    exportedSQL.stages.forEach(stage => {
      allSQL += stage.sql + '\n\n';
    });
    
    const blob = new Blob([allSQL], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pipelineName}_all_stages.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("All SQL stages downloaded!");
  };

  // Handle source type change - reset connection when type changes
  const handleSourceTypeChange = (newType) => {
    setSourceType(newType);
    setSourceConnectionId("");
    setSchemas([]);
    setColumnsByTable({});
  };

  // Check if ANY source type has connected connections
  const hasAnySourceConnections = oracleConnections.length > 0 || sqlserverConnections.length > 0 || postgresqlConnections.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader w-8 h-8" />
      </div>
    );
  }

  const hasNoConnections = !hasAnySourceConnections || bigqueryConnections.length === 0;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="pipeline-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/pipelines")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-xl font-semibold">{isEditing ? "Edit Pipeline" : "Create Pipeline"}</h2>
            <p className="text-sm text-muted-foreground">{sourceType === 'oracle' ? 'Oracle' : sourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'} → BigQuery data lake</p>
          </div>
        </div>
        {isEditing && (
          <Button variant="outline" size="sm" onClick={handleExportSQL} disabled={exportingSQLLoading}>
            {exportingSQLLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Code className="w-4 h-4 mr-2" />
            )}
            Export SQL
          </Button>
        )}
      </div>

      {hasNoConnections ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <AlertCircle className="empty-state-icon text-yellow-500" />
            <h3 className="font-semibold text-lg">Connections Required</h3>
            <p className="text-muted-foreground text-sm mt-1">Setup source database and BigQuery connections first.</p>
            <Button className="mt-4" onClick={() => navigate("/connections")}>Setup Connections</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Config */}
          <Card className="shadow-soft">
            <CardHeader><CardTitle className="text-base">Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Pipeline Name</Label>
                <Input value={pipelineName} onChange={(e) => setPipelineName(e.target.value)} placeholder="My Pipeline" data-testid="pipeline-name-input" />
              </div>
              
              {/* Source Type Selection */}
              <div className="space-y-2">
                <Label>Source Database Type</Label>
                <div className="flex gap-2">
                  {[
                    { id: 'oracle', label: 'Oracle', color: 'orange' },
                    { id: 'sqlserver', label: 'SQL Server', color: 'blue' },
                    { id: 'postgresql', label: 'PostgreSQL', color: 'teal' }
                  ].map((type) => (
                    <Button
                      key={type.id}
                      variant={sourceType === type.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSourceTypeChange(type.id)}
                      className={sourceType === type.id ? "" : `hover:bg-${type.color}-50`}
                      data-testid={`source-type-${type.id}`}
                    >
                      <Database className="w-4 h-4 mr-1" />
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label><Database className="w-4 h-4 inline mr-1" />Source ({sourceType === 'oracle' ? 'Oracle' : sourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'})</Label>
                  <Select value={sourceConnectionId} onValueChange={setSourceConnectionId}>
                    <SelectTrigger data-testid="source-connection-select"><SelectValue placeholder="Select connection" /></SelectTrigger>
                    <SelectContent>
                      {currentSourceConnections.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="flex items-center gap-2">
                            {c.name}
                            {c.status !== "connected" && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Disconnected</Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label><Cloud className="w-4 h-4 inline mr-1" />Destination (BigQuery)</Label>
                  <Select value={bigqueryConnectionId} onValueChange={setBigqueryConnectionId}>
                    <SelectTrigger data-testid="bigquery-connection-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {bigqueryConnections.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="flex items-center gap-2">
                            {c.name}
                            {c.status !== "connected" && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Disconnected</Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Connection Status Warning */}
              {sourceConnectionId && currentSourceConnections.find(c => c.id === sourceConnectionId)?.status !== "connected" && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Source connection is disconnected</span>
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    The selected source connection is not currently active. You can view saved fields but cannot fetch new column data. 
                    Reconnect the connection to add or modify fields.
                  </p>
                </div>
              )}

              {/* Data Strategy Selection */}
              <div className="space-y-2">
                <Label>Data Handling Strategy (for multiple schemas)</Label>
                <div className="flex gap-3">
                  <label className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all ${dataStrategy === 'override' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input 
                      type="radio" 
                      name="dataStrategy" 
                      value="override" 
                      checked={dataStrategy === 'override'} 
                      onChange={() => setDataStrategy('override')}
                      className="text-primary"
                    />
                    <div>
                      <div className="font-medium text-sm">Override (Default)</div>
                      <div className="text-xs text-muted-foreground">Replace all data for the tenant/unit when running any schema</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all ${dataStrategy === 'merge' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input 
                      type="radio" 
                      name="dataStrategy" 
                      value="merge" 
                      checked={dataStrategy === 'merge'} 
                      onChange={() => setDataStrategy('merge')}
                      className="text-primary"
                    />
                    <div>
                      <div className="font-medium text-sm">Merge</div>
                      <div className="text-xs text-muted-foreground">Keep data from other schemas, only replace data from the same schema</div>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Multi-Tenant Info */}
              <div className="border-t pt-4 mt-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-blue-700">Multi-Tenant Pipeline</span>
                  </div>
                  <p className="text-xs text-blue-600">
                    This pipeline is tenant-agnostic. When you run it, you&apos;ll select which tenant to run it for. 
                    The <code className="bg-white px-1 rounded">tenant_id</code> will be automatically injected into all data.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Flow */}
          <Card className="shadow-soft bg-muted/30">
            <CardContent className="py-4">
              <div className="flex items-center justify-center gap-2 text-sm">
                <Badge variant="outline" className="bg-orange-100 text-orange-700">Bronze (Raw)</Badge>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <Badge variant="outline" className="bg-slate-100 text-slate-700">Silver (Cleaned)</Badge>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <Badge variant="outline" className="bg-amber-100 text-amber-700">Gold (Business)</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Stages */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Stages</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => addStage("bronze")} className="bg-orange-50 border-orange-300 text-orange-700">
                  <Plus className="w-4 h-4 mr-1" />Bronze
                </Button>
                <Button variant="outline" size="sm" onClick={() => addStage("silver")} className="bg-slate-50 border-slate-300 text-slate-700" disabled={bronzeTables.length === 0}>
                  <Plus className="w-4 h-4 mr-1" />Silver
                </Button>
                <Button variant="outline" size="sm" onClick={() => addStage("gold")} className="bg-amber-50 border-amber-300 text-amber-700" disabled={silverTables.length === 0}>
                  <Plus className="w-4 h-4 mr-1" />Gold
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowSQLImportModal(true)} 
                  className="bg-amber-50 border-amber-300 text-amber-700"
                  disabled={!bigqueryConnectionId}
                  data-testid="import-gold-sql-btn"
                >
                  <FileCode className="w-4 h-4 mr-1" />Import SQL
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => setShowAIGoldModal(true)} 
                  className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
                  disabled={silverTables.length === 0}
                  data-testid="generate-gold-ai-btn"
                >
                  <Sparkles className="w-4 h-4 mr-1" />Generate Gold with AI
                </Button>
              </div>
            </div>

            {stages.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="empty-state py-12">
                  <Workflow className="empty-state-icon" />
                  <h3 className="font-semibold">Start with Bronze</h3>
                  <p className="text-muted-foreground text-sm">Add Bronze stages to extract tables from your source database</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {stages.map((stage, index) => {
                  if (stage.stage_type === "bronze") {
                    return (
                      <BronzeStageCard
                        key={index}
                        stage={stage}
                        stageIndex={index}
                        connectionId={sourceConnectionId}
                        bigqueryConnectionId={bigqueryConnectionId}
                        schemas={schemas}
                        columns={columnsByTable[`${stage.source_schema}.${stage.source_table}`] || []}
                        onUpdate={(u) => updateStage(index, u)}
                        onRemove={() => removeStage(index)}
                        onTableChange={fetchColumns}
                        onAddMultipleBronzeStages={addMultipleBronzeStages}
                        loadingColumns={loadingColumns}
                        sourceType={sourceType}
                      />
                    );
                  } else if (stage.stage_type === "silver") {
                    return (
                      <SilverStageCard
                        key={index}
                        stage={stage}
                        stageIndex={index}
                        bronzeTables={bronzeTables}
                        bronzeFieldsByTable={bronzeFieldsByTable}
                        bigqueryConnectionId={bigqueryConnectionId}
                        onUpdate={(u) => updateStage(index, u)}
                        onRemove={() => removeStage(index)}
                      />
                    );
                  } else if (stage.stage_type === "gold") {
                    // Compute gold tables created by earlier stages
                    const goldTablesFromPreviousStages = stages
                      .slice(0, index)
                      .filter(s => s.stage_type === 'gold' && s.target_table)
                      .map(s => s.target_table);
                    
                    // Build field info for gold tables
                    const goldFieldsByTableComputed = {};
                    
                    // Process Gold stages in order
                    stages.slice(0, index).forEach(s => {
                      if (s.stage_type === 'gold' && s.target_table) {
                        const outputFields = [];
                        const seenFields = new Set();
                        
                        // Helper to add field
                        const addField = (fieldName) => {
                          if (fieldName && !seenFields.has(fieldName)) {
                            seenFields.add(fieldName);
                            outputFields.push({ name: fieldName, target_field: fieldName });
                          }
                        };
                        
                        // 1. Fields from stage.fields (JOIN mode output)
                        (s.fields || []).forEach(f => {
                          addField(f.target_field || f.source_field?.split('.').pop());
                        });
                        
                        // 2. Fields from union_fields (UNION mode output)
                        (s.source_tables || []).forEach(st => {
                          if (st.union_fields && st.union_fields.length > 0) {
                            st.union_fields.forEach(uf => addField(uf.target_field));
                          }
                        });
                        
                        // 3. Calculated fields
                        (s.calculated_fields || []).forEach(calc => addField(calc.name));
                        
                        // 4. _source_table for UNION stages
                        if ((s.source_tables || []).some(st => st.role === 'union' || st.union_fields?.length > 0)) {
                          addField('_source_table');
                        }
                        
                        // 5. FALLBACK: If no explicit output fields, inherit from Silver sources
                        if (outputFields.length === 0) {
                          (s.source_tables || []).forEach(st => {
                            if ((st.source_layer || 'silver') === 'silver') {
                              const silverFields = silverFieldsByTable[st.table_name] || [];
                              silverFields.forEach(sf => addField(sf.name || sf.target_field));
                            } else if (st.source_layer === 'gold') {
                              // Inherit from another Gold table
                              const goldFields = goldFieldsByTableComputed[st.table_name] || [];
                              goldFields.forEach(gf => addField(gf.name || gf.target_field));
                            }
                          });
                        }
                        
                        goldFieldsByTableComputed[s.target_table] = outputFields;
                        console.log(`Computed fields for ${s.target_table}:`, outputFields.map(f => f.name));
                      }
                    });
                    
                    console.log('goldFieldsByTableComputed keys:', Object.keys(goldFieldsByTableComputed));
                    console.log('goldTablesFromPreviousStages:', goldTablesFromPreviousStages);
                    
                    // Handler for SQL import from within a Gold stage
                    const handleImportSQL = () => {
                      setShowSQLImportModal(true);
                    };
                    
                    return (
                      <GoldStageCardV2
                        key={index}
                        stage={stage}
                        stageIndex={index}
                        silverTables={silverTables}
                        goldTables={goldTablesFromPreviousStages}
                        silverFieldsByTable={silverFieldsByTable}
                        goldFieldsByTable={goldFieldsByTableComputed}
                        bigqueryConnectionId={bigqueryConnectionId}
                        existingBigQueryGoldTables={existingBigQueryGoldTables}
                        onUpdate={(u) => updateStage(index, u)}
                        onRemove={() => removeStage(index)}
                        onImportSQL={handleImportSQL}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>

          {/* Save */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate("/pipelines")}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Pipeline
            </Button>
          </div>
        </>
      )}

      {/* AI Gold Generation Modal */}
      <Dialog open={showAIGoldModal} onOpenChange={setShowAIGoldModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Generate Gold Layer with AI
            </DialogTitle>
            <DialogDescription>
              Describe your analytics use case and AI will create an optimized Gold layer configuration
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* API Key Input */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                OpenAI API Key
              </Label>
              <Input
                type="password"
                value={aiApiKey}
                onChange={(e) => setAiApiKey(e.target.value)}
                placeholder="sk-..."
                data-testid="ai-api-key-input"
              />
              <p className="text-xs text-muted-foreground">
                Your API key is stored locally and never sent to our servers
              </p>
            </div>
            
            {/* Use Case Input */}
            <div className="space-y-2">
              <Label>Describe Your Use Case</Label>
              <DebouncedTextarea
                value={aiUseCase}
                onChange={setAiUseCase}
                delay={500}
                placeholder="E.g., 'Create a sales analytics fact table that combines invoice sales and returns (with returns as negative values), then joins with customer, product, cost center, and branch dimensions. Include date fields (year, month, quarter, day_of_week) for time-series predictions and forecasting. Calculate profit margins and total sales.'"
                className="min-h-[100px]"
                data-testid="ai-use-case-input"
              />
              <p className="text-xs text-muted-foreground">
                💡 <strong>Tips:</strong> Mention &quot;time series&quot; or &quot;predictions&quot; to include date extractions (year, month, quarter). 
                Mention &quot;union&quot; or &quot;combine sales and returns&quot; for multi-stage Gold with per-table field mappings.
              </p>
            </div>
            
            {/* Silver Table Selection */}
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span>Select Silver Tables to Include:</span>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setAiSelectedTables(silverTables)}
                  >
                    Select All
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setAiSelectedTables([])}
                  >
                    Clear
                  </Button>
                </div>
              </Label>
              <div className="p-3 bg-slate-50 rounded-lg max-h-[200px] overflow-y-auto">
                {silverTables.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {silverTables.map(t => (
                      <label key={t} className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={aiSelectedTables.includes(t)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAiSelectedTables([...aiSelectedTables, t]);
                            } else {
                              setAiSelectedTables(aiSelectedTables.filter(x => x !== t));
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm truncate">{t}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No Silver tables available. Create Silver stages first.</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {aiSelectedTables.length === 0 
                  ? "All Silver tables will be used (select specific ones to focus the AI)"
                  : `${aiSelectedTables.length} table(s) selected`
                }
              </p>
            </div>
            
            {/* AI Suggestion Preview - Multi-Stage */}
            {aiIsMultiStage && aiGoldStages.length > 0 && (
              <div className="space-y-3 border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-indigo-50">
                <div className="flex items-center justify-between">
                  <Label className="text-purple-700 font-semibold">AI Multi-Stage Suggestion</Label>
                  <Badge className="bg-indigo-100 text-indigo-700">{aiGoldStages.length} Gold Stages</Badge>
                </div>
                
                <div className="space-y-4">
                  {aiGoldStages.map((stageSuggestion, stageIdx) => (
                    <div key={stageIdx} className="border rounded-lg p-3 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-amber-500 text-white">Stage {stageIdx + 1}</Badge>
                        <span className="font-medium">{stageSuggestion.target_table}</span>
                      </div>
                      
                      {stageSuggestion.description && (
                        <p className="text-sm text-slate-600 mb-2">{stageSuggestion.description}</p>
                      )}
                      
                      <div className="text-sm mb-2">
                        <span className="font-medium">Source Tables: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(stageSuggestion.source_tables || []).map((table, i) => {
                            const tableName = typeof table === 'string' ? table : table.table_name;
                            const role = typeof table === 'object' ? table.role : 'join';
                            const layer = typeof table === 'object' ? table.source_layer : 'silver';
                            const hasUnionFields = typeof table === 'object' && table.union_fields && table.union_fields.length > 0;
                            return (
                              <Badge 
                                key={i} 
                                variant="outline" 
                                className={`text-xs ${
                                  layer === 'gold' 
                                    ? 'bg-amber-50 border-amber-400 text-amber-700' 
                                    : role === 'union' 
                                      ? 'bg-blue-50 border-blue-300 text-blue-700' 
                                      : 'bg-green-50 border-green-300 text-green-700'
                                }`}
                              >
                                {layer === 'gold' ? '🔶' : ''} {tableName} ({role})
                                {hasUnionFields && ` [${table.union_fields.length} fields]`}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                      
                      {(stageSuggestion.join_conditions || []).length > 0 && (
                        <div className="text-xs text-slate-500">
                          Joins: {stageSuggestion.join_conditions.map(j => `${j.left_table}↔${j.right_table}`).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Feedback for rejection */}
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-sm">Feedback (if rejecting)</Label>
                  <DebouncedTextarea
                    value={aiFeedback}
                    onChange={setAiFeedback}
                    delay={500}
                    placeholder="Tell AI what to change..."
                    className="min-h-[60px]"
                  />
                </div>
              </div>
            )}
            
            {/* AI Suggestion Preview - Single Stage (Legacy) */}
            {!aiIsMultiStage && aiGoldSuggestion && (
              <div className="space-y-3 border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <Label className="text-purple-700 font-semibold">AI Suggestion</Label>
                  <Badge className="bg-green-100 text-green-700">Ready for Review</Badge>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Target Table:</span>
                    <Badge variant="outline">{aiGoldSuggestion.target_table}</Badge>
                  </div>
                  
                  {aiGoldSuggestion.description && (
                    <p className="text-sm text-slate-600">{aiGoldSuggestion.description}</p>
                  )}
                  
                  <div className="text-sm">
                    <span className="font-medium">Source Tables: </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(aiGoldSuggestion.source_tables || []).map((table, i) => {
                        const tableName = typeof table === 'string' ? table : table.table_name;
                        const role = typeof table === 'object' ? table.role : 'join';
                        return (
                          <Badge 
                            key={i} 
                            variant="outline" 
                            className={`text-xs ${role === 'union' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-green-50 border-green-300 text-green-700'}`}
                          >
                            {tableName} ({role})
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                  
                  {(aiGoldSuggestion.join_conditions || aiGoldSuggestion.joins || []).length > 0 && (
                    <div className="text-sm">
                      <span className="font-medium">Joins: </span>
                      {(aiGoldSuggestion.join_conditions || aiGoldSuggestion.joins || []).map((j, i) => (
                        <span key={i} className="inline-block bg-white px-2 py-1 rounded mr-1 mb-1 text-xs">
                          {j.left_table}.{j.left_field} {j.join_type} {j.right_table}.{j.right_field}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="text-sm">
                    <span className="font-medium">Fields ({(aiGoldSuggestion.fields || []).length}): </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(aiGoldSuggestion.fields || []).slice(0, 10).map((f, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {f.target_field}
                        </Badge>
                      ))}
                      {(aiGoldSuggestion.fields || []).length > 10 && (
                        <Badge variant="outline" className="text-xs">
                          +{(aiGoldSuggestion.fields || []).length - 10} more
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {aiGoldSuggestion.aggregations && aiGoldSuggestion.aggregations.length > 0 && (
                    <div className="text-sm">
                      <span className="font-medium">Aggregations: </span>
                      {aiGoldSuggestion.aggregations.join(", ")}
                    </div>
                  )}
                  
                  {aiGoldSuggestion.reasoning && (
                    <div className="mt-2 p-2 bg-white rounded text-sm text-slate-600">
                      <span className="font-medium">AI Reasoning: </span>
                      {aiGoldSuggestion.reasoning}
                    </div>
                  )}
                </div>
                
                {/* Feedback for rejection */}
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-sm">Feedback (if rejecting)</Label>
                  <DebouncedTextarea
                    value={aiFeedback}
                    onChange={setAiFeedback}
                    delay={500}
                    placeholder="Tell AI what to change, e.g., 'Add customer segmentation fields' or 'Use monthly aggregation instead of daily'"
                    className="min-h-[60px]"
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            {!aiGoldSuggestion && !aiIsMultiStage ? (
              <div className="w-full">
                {aiGenerating && (
                  <p className="text-xs text-muted-foreground text-center mb-2">
                    ⏳ Analyzing {silverTables.length} tables and generating multi-stage Gold configuration... This may take 1-2 minutes.
                  </p>
                )}
                <Button 
                  onClick={generateGoldWithAI} 
                  disabled={aiGenerating || !aiApiKey.trim() || !aiUseCase.trim() || silverTables.length === 0}
                  className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
                  data-testid="generate-gold-btn"
                >
                  {aiGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Multi-Stage Gold...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Gold Configuration
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 w-full justify-end">
                <Button 
                  variant="outline" 
                  onClick={rejectGoldSuggestion}
                  disabled={aiGenerating || !aiFeedback.trim()}
                >
                  {aiGenerating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Regenerate with Feedback
                </Button>
                <Button 
                  onClick={approveGoldSuggestion}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="approve-gold-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {aiIsMultiStage ? `Add ${aiGoldStages.length} Stages` : 'Approve & Add to Pipeline'}
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SQL Import Modal */}
      <SQLImportModal
        isOpen={showSQLImportModal}
        onClose={() => setShowSQLImportModal(false)}
        bigqueryConnectionId={bigqueryConnectionId}
        onImport={(importedStages) => {
          // Add imported stages to the pipeline
          const newStages = importedStages.map(s => ({
            stage_type: 'gold',
            target_table: s.target_table || '',
            source_tables: s.source_tables || [],
            join_conditions: s.join_conditions || [],
            fields: s.fields || [],
            calculated_fields: s.calculated_fields || [],
            group_by_fields: s.group_by_fields || [],
            filter_condition: s.filter_condition || '',
            partition_by: s.partition_by || '',
            cluster_by: s.cluster_by || ''
          }));
          setStages(prev => [...prev, ...newStages]);
          toast.success(`Imported ${newStages.length} Gold stage${newStages.length > 1 ? 's' : ''}`);
        }}
      />

      {/* SQL Export Modal */}
      <Dialog open={showSQLExportModal} onOpenChange={setShowSQLExportModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-blue-500" />
              Export BigQuery SQL
            </DialogTitle>
            <DialogDescription>
              Download or copy SQL scripts for each stage. Run these directly in BigQuery console if portal is unavailable.
            </DialogDescription>
          </DialogHeader>

          {exportingSQLLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <span className="ml-3 text-muted-foreground">Generating SQL...</span>
            </div>
          ) : exportedSQL ? (
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              {/* Pipeline Info */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-blue-900">{exportedSQL.pipeline_name}</h4>
                    <p className="text-sm text-blue-700">Project: {exportedSQL.bq_project}</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Exported: {new Date(exportedSQL.exported_at).toLocaleString()}
                    </p>
                  </div>
                  <Button onClick={handleDownloadAllSQL} size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Download All Stages
                  </Button>
                </div>
              </div>

              {/* SQL Stages */}
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-4">
                  {exportedSQL.stages.map((stage, idx) => (
                    <Card key={idx} className="border-2">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={stage.stage_type === 'bronze' ? 'default' : stage.stage_type === 'silver' ? 'secondary' : 'outline'}
                              className="capitalize"
                            >
                              {stage.stage_type}
                            </Badge>
                            <CardTitle className="text-base">{stage.stage_name}</CardTitle>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleCopySQL(stage.sql)}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDownloadSQL(stage.stage_name, stage.sql)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                        {stage.description && (
                          <p className="text-sm text-muted-foreground mt-1">{stage.description}</p>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="relative">
                          <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-[300px] overflow-y-auto">
                            <code>{stage.sql}</code>
                          </pre>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>

              {/* Instructions */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h5 className="font-semibold text-amber-900 text-sm mb-2">📝 How to Use</h5>
                <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
                  <li><strong>Bronze Stage</strong>: Load source data to BigQuery first (using external tools)</li>
                  <li><strong>Silver Stage</strong>: Run the transformation SQL in BigQuery console</li>
                  <li><strong>Gold Stage</strong>: Run the aggregation SQL to create analytics tables</li>
                  <li>Execute stages in order: Bronze → Silver → Gold</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No SQL exported yet
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowSQLExportModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
